/**
 * SlopCraft Web — main entry.
 *
 * Wires together the worker-pooled chunk pipeline, greedy-meshed rendering,
 * flood-fill lighting, sky/day-night cycle, player physics and the survival
 * game loop on top of them: inventory, mining with tools, drops, crafting /
 * furnace / chest screens, mobs, combat, health and hunger, menus.
 */

import * as THREE from 'three';
import { SEED } from './constants';
import { buildAtlas } from './textures';
import { createVoxelMaterials } from './render/materials';
import { bakeGrassSideMask } from './render/atlasOverlays';
import { setBlendRadius } from './biomeTint';
import { createEnvUniforms } from './render/env';
import { ENTITY_LIGHT } from './render/entities/core';
import { buildLiquidTextures } from './render/liquid';
import { buildFireTextures } from './render/fire';
import { PostFX } from './render/post';
import { ShadowCascades } from './render/shadows';
import { Sky } from './render/sky';
import { PRESETS, loadPrefs, savePrefs, type Quality } from './settings';
import { ChunkManager } from './world/manager';
import { WorldSave } from './world/save';
import { Player, raycast, type RayHit } from './player/player';
import { PerfHud, PerfMode } from './ui/perf';
import { PERF_FLAGS } from './perf/flags';
import { TerrainGenerator, BIOME_NAMES, Biome } from './worldgen/terrain';
import type { StructureHit } from './worldgen/structures';
import { hash2, mulberry32 } from './noise';
import {
  AIR, BED_FOOT_ID, BED_HEAD_ID, chestClosedOf, chestFacing, chestOpenOf,
  CACTUS_ID, CHEST_FACING_0, CHEST_OPEN_FACING_0, CRAFTING_TABLE_ID, DEFAULT_HOTBAR,
  collisionOf, SHAPE_FAMILIES, shapeId, DOOR_STATE, FIRE_ID,
  FURNACE_FACINGS, isFurnaceBlock, isChestOpen, IS_SOLID, IS_TARGETABLE, PLANKS_ID, TARGET_BOXES, PLANT_JITTER,
  TNT_ID, TORCH_ID, WATER_ID,
} from './blocks';
import { FLINT_AND_STEEL_ID, isItem, loadItemIcons, nameOf } from './items';
import { FluidSim } from './world/fluid';
import { FireSim } from './world/fire';
import { TntSim, isFlashing } from './world/tnt';
import { RedstoneSim } from './world/redstone/sim';
import { countEntities, sunSignal } from './world/redstone/hostUtil';
import { redstoneSound } from './world/redstone/sounds';
import { CHUNK_SIZE, WORLD_HEIGHT, REACH, TICK_DT, SEA_LEVEL } from './constants';
import { ChunkState } from './world/chunk';
import { BlockParticles } from './render/particles';
import { FX } from './game/effects/effects';
import { wardenHooks } from './entities/boss/wardenHooks';
import { heartStyleOf } from './ui/heartStyle';
import { critBurst, enchantHitBurst, eatCrumbs, landingDustCount } from './render/playerFx';
import { buildParticleTextures } from './render/particleTextures';
import { PrimedTntRenderer } from './render/primedTnt';
import { FallingBlockRenderer } from './render/fallingBlocks';
import { FallingSim } from './world/falling';
import { AmbientParticles } from './render/ambient';

// survival game loop
import { GameSettings } from './game/gameSettings';
import { Inventory, HOTBAR } from './game/inventory';
import { I } from './game/itemIds';
import { RocketField, fireworkExplode, rocketDuration, rocketStars, type FwTarget } from './game/fireworks';
import { BED_MESSAGES, bedVerdict } from './game/itemUse/bed';
import { SleepTimer, lyingPose, skipToDawn, sleepDecision, sleepHooks, sleepStatusText, type LyingPose } from './game/sleep';
import { SleepOverlay } from './ui/sleepOverlay';
import { bedDirection } from './entities/village/villager';
import { popTotem } from './game/itemUse/totem';
import { equipHeld } from './game/itemUse/equip';
import { itemDef, spriteOf } from './game/itemdb';
import {
  blockItemOf, breakSeconds,
} from './game/mining';
import { rollChestLoot } from './game/loot';
import { FurnaceWorld } from './game/furnace';
import { furnaceEffects, reconcileFurnaceBlocks, setFurnaceBlockLit } from './game/furnaceBlocks';
import { BrewingWorld } from './game/brewing/stand';
import { PotionSim, type SplashCtx } from './game/brewing/splash';
import { isDrinkable, drinkStack, applyEffectList, emptiedBy } from './game/brewing/use';
import { attackBonus, mineMult, speedMult, jumpBoost, slowFalling, nightVision, giveMobEffect, visibilityScale } from './game/effects/effects';
import { applyFoodEffects, eatTicksOf, mobHitEffects, STRAY_ARROW_EFFECT } from './game/effects/sources';
import { BREWING_STAND_ID } from './blocks';
import { PotionRenderer } from './render/potions';
import { EffectsHud } from './ui/effectsHud';
import { EXHAUST_ATTACK, PlayerStats } from './game/playerStats';
import {
  clearStack, emptyStack, isEmpty, setStack, stackOf, type DamageSource, type GameMode, type Stack,
} from './game/types';
import { GuiStage, loadKit } from './ui/kit';
import { ItemIcons } from './ui/itemIcons';
import { GameHud, type LocatorWaypoint } from './ui/gameHud';
import { MenuHost } from './ui/menus';
import { ScreenHost } from './ui/screens/screenHost';
import { TeleportMenu } from './ui/teleportMenu';
import { getChatOverlay } from './ui/chat';
import { commandsUnlocked, isSecretCommand, parseCommand, runChatCommand, unlockCommands as unlockChatCommands, type CommandContext } from './ui/chatCommands';
import { EntityManager, type EntityHost, type Mob, type PlayerTarget } from './entities';
import { ItemEntityManager } from './entities/itemEntities';
import { VillageSystem } from './entities/village/system';
import { VillagerTradeScreen } from './entities/village/tradeScreen';
import { mountMobAsPlayer, initMounts, mountUse, rideUseItem, ridingAfterMobs, mountState, dismountRider, type MountEnv } from './game/mounts';
import { HorseScreen } from './ui/horseScreen';
import { LlamaScreen } from './ui/llamaScreen';
import { TraderSystem } from './entities/trader/system';
import { spits } from './entities/trader/spit';
import { SpitRenderer } from './render/spitRenderer';
import { maxStackOf } from './game/itemdb';
import { RideHud } from './ui/rideHud';
import { XpOrbManager } from './entities/xp';
import { Hearts } from './render/hearts';
import { CropSim, trampleCheck } from './world/crops';
import { cropDrops, isCrop } from './game/farming';
import { dropsFor as popDrops } from './game/mining';
import { ThrownSim } from './entities/thrown';
import { ThrownRenderer } from './render/thrown';
import { BobberSim, type BobberCtx } from './entities/bobber';
import { BobberRenderer } from './render/bobber';
import { spawnBaby } from './entities/breeding';
import { GRASS_ID, DIRT_ID, LAVA_ID, FLOWING_LAVA_IDS } from './blocks';
import { eggSpawnPos, isSpawnEgg, spawnEggMob } from './game/spawnEggs';
import { deserializeEntities, serializeEntities, type EntityStores } from './entities/persist';
import { EntityRenderer, MOB_TYPES, PlayerModel, PlayerPreview } from './render/entities';
import { itemSpriteMesh } from './render/entities/extras';
import { createLitMaterial, ItemEntityRenderer, setMeshLight } from './render/itemEntities';
import { tridentGeometry, TRIDENT_TEX } from './render/tridentModel';
import { applyThirdPerson, heldKindOfItem } from './render/heldItems';
import { FirstPersonHand } from './render/firstPerson';
import { BreakOverlay } from './render/breakOverlay';
import { BlockOutline } from './render/blockOutline';
import { plantJitterX, plantJitterZ } from './shapes';
import { ShieldView } from './render/shield';
import { enchantDamageBonus } from './game/enchant/effects';
import {
  isSpear, isSpearId, jabReady, jabTargets, rayBoxDist, chargeStage, chargeDamage, chargeKnockbackScale, relativeSpeedBps, spearTier, chargeStageIndex, CHARGE_MIN_KB_BPS,
  CHARGE_REACH, CHARGE_MIN_DAMAGE, lungeLevel, lungeBlocked, lungeVelocity, lungeExhaustion, lungeExtraDurability,
} from './game/spear';
import { COMBAT_EDITION, meleeHit, bowPower, weaponAttack, chargeScale, windBurstVelocity } from './game/combat';
import { armorEPF, knockbackResistance, mendingAbsorb, thornsDamage, wearStack } from './game/enchant/playerEnchants';
import { enchLevel } from './game/enchant/stack';
import { E as ENCH } from './game/enchant/registry';
import { countBookshelves } from './game/enchant/table';
import { SweepFx } from './render/sweepFx';
import { TridentManager, type TridentHost } from './entities/trident';
import { crossbowHasAmmo, loadCrossbow, fireCrossbowAt, shootBowAt, throwTridentAt, tridentUsable, type ProjEnv } from './game/projectileUse';
import { TridentRenderer } from './render/tridentRenderer';
import { BoatRenderer } from './render/boat';
import { MinecartSystem } from './entities/vehicles/minecartGlue';
import { MinecartRenderer } from './render/minecart';
import { createBoats } from './game/boatUse';
import { EnchantBookRenderer } from './render/enchantBook';
import { tickGlint } from './render/glint';
import { ANVIL_FAMILIES, anvilState, BARREL_ID, GRINDSTONE_ID, ENCHANTING_TABLE_ID, SMITHING_TABLE_ID } from './blocks';
import { END_PORTAL_FRAME_EYE_ID } from './blocks';
import { attachAudio } from './audio';
import { DimensionTravel } from './game/dimensionTravel';
import { LavaSim } from './world/lava';
import { blockUnderFeet, magmaDamage } from './game/magma';
import { DimensionEnv } from './render/dimensionEnv';
import { weather } from './game/weather';
import { BOLT_DAMAGE, BOLT_RADIUS, columnTop, lightningConversion, WeatherEffects } from './game/weatherWorld';
import { WeatherRenderer } from './render/weather';
import { EnvAmbience, NetherParticles } from './render/biomeAmbience';
import { NETHER_BIOME_NAMES, NetherGenerator } from './worldgen/nether';
import { inFortress } from './worldgen/netherFortress';
import { inBastion, netherLootKind } from './worldgen/netherStructures';
import { endLootKind } from './worldgen/end';
import { TrialRuntime } from './game/trials';
import { chorusTeleport, GatewayRuntime } from './game/endOuter';
import { VAULT_ID, VAULT_OPENED_ID } from './blocks';
import { netherHooks } from './entities/nether/mobs';
import { angerPiglinsNear, piglinHooks, PIGLIN_ANGERING_BLOCKS } from './entities/nether/barter';
import { lineClear } from './entities/host';
import { overworld2Hooks } from './entities/overworld2/mobs';
import { isRightClickable } from './entities/overworld2/interact';
import { leashes, leadUseOnMob, leadUseOnFence, isFence, type LeadUseEnv } from './entities/leash';
import { LeashRenderer } from './render/leash';
import { witchHooks } from './entities/overworld2/witch';
import { NetherSpawner } from './entities/nether/spawning';
import { NetherRuntime } from './game/netherRuntime';
import { WitherRuntime } from './game/witherRuntime';
import { EndRuntime } from './game/endRuntime';
import { EndFightGlue } from './game/endFightGlue';
import { SOUL_FIRE_ID } from './blocks';
import { isHostileType } from './entities';
import { installNet, prepareNetSave, type NetController } from './net';
import { connectServer, prepareServerSave, installServerMode } from './client/install';
import { chooseServer, configuredServer, joinConfiguredServer } from './client/entry';
import { installBlocks } from './client/blocks';
import * as blockRules from './game/blockActions';
import { installWindows } from './client/windows';
import { containerKind } from './game/menu';
import { ServerReplica } from './client/entities';
import { ProjectileDrawer } from './client/projectiles';
import { TileDrawer } from './client/tiles';
import { installCombat } from './client/combat';
import { BossBar } from './ui/bossBar';
import { PF_SNEAK } from './net/codec';
import type { RemotePlayerView } from './net/session';
import { itemUseLikely, useItemOnMob, useOnMob, usePet, type MobUseFx, type MobUseWorld } from './entities/mobUse';
import { initTouchControls } from './ui/touchControls';
import { installAgentApi } from './agent/agentApi';
import { SPAWN_RADIUS, pickSpawn } from './game/spawn';

const app = document.getElementById('app')!;

// Dev / visual-test switches: ?menu=0 skips the title screen.
const urlParams = new URLSearchParams(location.search);
const AUTOSTART_KEY = 'opencraft.autostart';
let autostart = false;
try {
  autostart = sessionStorage.getItem(AUTOSTART_KEY) === '1';
  sessionStorage.removeItem(AUTOSTART_KEY);
} catch { /* storage unavailable */ }

// --- error capture (shown under the perf HUD) -------------------------------
const errLines: string[] = [];
window.addEventListener('error', (e) => {
  errLines.unshift('ERR ' + e.message);
  errLines.length = Math.min(errLines.length, 6);
});
window.addEventListener('unhandledrejection', (e) => {
  errLines.unshift('REJ ' + String((e as PromiseRejectionEvent).reason).slice(0, 180));
  errLines.length = Math.min(errLines.length, 6);
});
const origConsoleError = console.error.bind(console);
console.error = (...args: unknown[]) => {
  errLines.unshift('CERR ' + args.map((a) => String(a)).join(' ').slice(0, 220));
  errLines.length = Math.min(errLines.length, 6);
  origConsoleError(...args);
};

// --- options ----------------------------------------------------------------
const settings = new GameSettings();
const presetOf = (): Quality => ({ ...PRESETS[settings.graphics], viewRadius: settings.renderDistance });
let quality = presetOf();

// --- renderer ---------------------------------------------------------------
// The scene renders into an HDR target (PostFX owns MSAA + tonemapping), so
// the canvas itself needs neither antialiasing nor a depth buffer.
const renderer = new THREE.WebGLRenderer({ antialias: false, depth: false, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.pixelRatioCap));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
// several render passes per frame (shadows, scene, post) — count them all
renderer.info.autoReset = false;
app.appendChild(renderer.domElement);

// --- textures & materials ---------------------------------------------------
const atlas = await buildAtlas();
bakeGrassSideMask(atlas.canvas);
const atlasTexture = new THREE.CanvasTexture(atlas.canvas);
atlasTexture.flipY = false;
atlasTexture.magFilter = THREE.NearestFilter;
atlasTexture.minFilter = THREE.LinearMipmapLinearFilter;
atlasTexture.generateMipmaps = true;
// Tiles are authored in sRGB; the GPU decodes them so lighting and mip
// filtering happen in linear space.
atlasTexture.colorSpace = THREE.SRGBColorSpace;
atlasTexture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());

const env = createEnvUniforms();
env.uGamma.value = settings.brightness;
const liquid = await buildLiquidTextures();
// animated fire sheets (vanilla fire_0/fire_1 frame strips) — see render/fire.ts
const fire = await buildFireTextures();
// vanilla particle sprites (smoke + explosion frame sequences) — see render/particleTextures.ts
const particleSprites = await buildParticleTextures();
const shadows = new ShadowCascades(renderer, { cascades: quality.shadowCascades, mapSize: quality.shadowMapSize });
const post = new PostFX(renderer, { bloom: quality.bloom, msaa: quality.msaa });
const voxel = createVoxelMaterials(atlasTexture, liquid, fire, env, shadows.uniforms, quality);

// --- world / chunks ---------------------------------------------------------
// Persisted world state: player edits, player position, time of day. Loaded
// here so chunk generation re-applies edits and the player resumes in place.
const save = new WorldSave(SEED);
// server mode (docs/server-model.md): `?server=ws://host:port` plays on a game
// server. `srv` is the live session, null offline; client/install.ts is the glue.
// A build made for a production server (VITE_GAME_SERVER) gets here through the Multiplayer button (client/entry.ts).
const srv = await connectServer(chooseServer(location.search), quality.viewRadius + 1);
/** The seed everything seed-derived on this page uses: the server's world in server mode. */
const WORLD_SEED = srv ? srv.seed : SEED;
// multiplayer: this page plays in a room, so the save uses that room's world
// namespace plus this tab's own player state (a second tab is a clean player).
if (srv) prepareServerSave(save, srv); else await prepareNetSave(save);
const chunks = new ChunkManager(WORLD_SEED, voxel, quality.viewRadius, srv ? undefined : save, quality.biomeBlend, srv !== null);
// perf: region-batched chunk meshes (src/perf/flags.ts toggles)
if (PERF_FLAGS.batch) chunks.enableBatching(renderer.getContext() as WebGL2RenderingContext, PERF_FLAGS.batchTranslucent);
const player = new Player(chunks.world, renderer.domElement);
// dimensions: the save remembers which one the player is in (world/dimension.ts)
if (save.dimension !== 'overworld') chunks.setDimension(save.dimension);
const netherGen = new NetherGenerator(WORLD_SEED); // biome lookups for the nether fog tint
const travel = new DimensionTravel({
  chunks, save, player,
  creative: () => mode === 'creative',
  beforeSwitch: () => {
    save.setFurnaces(furnaces.serialize());
    brewingBlob();
    leashes.releasePlayerHeld(true);   // mobs cannot follow through a portal: the leads drop
    if (entitiesRestored) save.entities[chunks.dimension] = { ...serializeEntities(entityStores()), boats: boats.serialize(), falling: falling.serialize() };
  },
  afterSwitch: (to) => {
    furnaces.load(save.furnaces);
    brewing.load(brewingRaw, to);
    entities.clear();
    netherRt.clear();
    witherRt.clear();
    trials.clear();
    endRt.clear();
    if (entitiesRestored) { deserializeEntities(entityStores(), save.entities[to]); boats.load((save.entities[to] as { boats?: unknown } | undefined)?.boats); falling.load((save.entities[to] as { falling?: unknown } | undefined)?.falling); }
    // overworld mobs do not belong in the nether (nether spawns: TODO entities/nether)
    entities.spawningEnabled = to === 'overworld';
  },
  homeSpawn: () => {
    const sp = save.spawn ?? (worldSpawn ? { x: worldSpawn.x + 0.5, y: undefined, z: worldSpawn.z + 0.5 } : null);
    if (sp) return { x: sp.x, z: sp.z, y: sp.y };
    const w = hudTerrain.findSpawn();
    return { x: w.x + 0.5, z: w.z + 0.5 };
  },
  toast: (m) => hud.toast(m),
});
travel.attachOverlay(document.body, post.wobble);

// water: every block change wakes the water around it (see world/fluid.ts)
const fluids = new FluidSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
});
// fire: spreads through flammable blocks and burns out (see world/fire.ts)
const fires = new FireSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
});
// TNT: a lit charge burns a 4 s fuse, then blasts a crater (see world/tnt.ts)
const tnt = new TntSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
});
// falling sand / gravel / anvils / dragon egg (world/falling.ts); the server runs it in server mode
const falling = new FallingSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  drop: (id, x, y, z) => { items.spawnBlockDrop(stackOf(id), x, y, z); },
  hurt: (box, amount) => {
    const [x0, y0, z0, x1, y1, z1] = box;
    for (const m of entities.mobs.slice()) {
      if (!m.alive || m.removed) continue;
      const h = m.width / 2;
      if (m.x + h <= x0 || m.x - h >= x1 || m.z + h <= z0 || m.z - h >= z1 || m.y + m.height <= y0 || m.y >= y1) continue;
      entities.attack(m, amount, (x0 + x1) / 2, (z0 + z1) / 2, 0);
    }
    const p = player.position;
    if (mode === 'survival' && p.x + 0.3 > x0 && p.x - 0.3 < x1 && p.z + 0.3 > z0 && p.z - 0.3 < z1 && p.y < y1 && p.y + 1.8 > y0) stats.damage(amount, { kind: 'generic' });
  },
  fx: (k, x, y, z, id) => { playBlockFx(k === 'land' || k === 'anvil_land' ? 'place' : 'break', x, y, z, id); },
});
// redstone (world/redstone): offline the world runs the sim the server runs
const redstone = new RedstoneSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  fx: (k, x, y, z, d) => { playBlockFx(k as blockRules.BlockFx, x, y, z, d); },
  entities: (x0, y0, z0, x1, y1, z1) => countEntities({ players: [player.position], mobs: entities.mobs, items: items.items, carts: minecarts.sim.carts }, x0, y0, z0, x1, y1, z1),
  pushEntities: (x, y, z, dx, dy, dz) => {
    const p = player.position;
    if (p.x + 0.3 > x && p.x - 0.3 < x + 1 && p.z + 0.3 > z && p.z - 0.3 < z + 1 && p.y < y + 1 && p.y + 1.8 > y) p.set(p.x + dx, p.y + dy, p.z + dz);
  },
  daylight: () => (chunks.dimension === 'overworld' ? sunSignal(sky.time) : 0),
  primeTnt: (x, y, z) => { tnt.prime(x, y, z); },
  popBlock: (x, y, z, id) => { for (const s of popDrops(id, 0, Math.random)) items.spawnBlockDrop(s, x, y, z); },
}, chunks.world);
/** Set once the enchanting-table book renderer exists (block changes can arrive before that). */
let bookBlockHook: ((x: number, y: number, z: number) => void) | null = null;
// plants / crops / torches that lose their support (or wash away) drop their loot, like vanilla
chunks.onPopped = (x, y, z, old) => {
  if (srv) return; // the server drops it
  for (const s of popDrops(old, 0, Math.random)) items.spawnBlockDrop(s, x, y, z);
};
chunks.onBlockChange = (x, y, z) => {
  bookBlockHook?.(x, y, z);
  if (srv) return; // the server runs the block sims
  fluids.blockChanged(x, y, z);
  fires.blockChanged(x, y, z);
  tnt.blockChanged(x, y, z);
  falling.blockChanged(x, y, z);
  lava.blockChanged(x, y, z);
  redstone.blockChanged(x, y, z);
  travel.blockChanged(x, y, z);
};
// lava flows like vanilla: slower and shorter in the overworld than in the nether
const lava = new LavaSim({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  nether: () => chunks.dimension === 'nether',
  ignite: (x, y, z) => fires.ignite(x, y, z),
  rnd: Math.random,
});
const fwBurst = (x: number, y: number, z: number, cols: readonly number[]): void => {
  for (const c of cols) for (let k = 0; k < 24; k++) {
    const th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1), sp = 2 + Math.random();
    particles.spark(x, y, z, Math.sin(ph) * Math.cos(th) * sp, Math.cos(ph) * sp, Math.sin(ph) * Math.sin(th) * sp,
      ((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255);
  }
};
const fwPlayer: FwTarget = {
  get x() { return player.position.x; }, get y() { return player.position.y; }, get z() { return player.position.z; }, height: 1.8,
  hurt: (d) => { if (mode === 'survival') stats.damage(d, { kind: 'explosion' }); },
};
const rockets: RocketField = new RocketField({
  targets: () => [fwPlayer, ...entities.mobs.filter((m) => !m.removed).map((m): FwTarget => ({
    get x() { return m.x; }, get y() { return m.y; }, get z() { return m.z; }, height: m.height,
    hurt: (d, fx, fz) => { entities.attack(m, d, fx, fz, 0.4); },
  }))],
  solid: (x, y, z) => IS_SOLID[chunks.world.getBlock(x, y, z)] === 1,
  blocked: (x0, y0, z0, x1, y1, z1) => {
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0, len = Math.hypot(dx, dy, dz);
    if (len < 0.5) return false;
    const h = raycast(chunks.world, { x: x0, y: y0, z: z0 } as never, { x: dx / len, y: dy / len, z: dz / len } as never, len);
    return !!h;
  },
  trail: (x, y, z) => { particles.fuse(x, y, z); },
  burst: fwBurst,
  sound: (id, x, y, z) => sfx.play(id, { x, y, z }),
});
player.glider.onRocketTick = () => { particles.fuse(player.position.x + (Math.random() - 0.5) * 0.4, player.position.y + 0.9, player.position.z + (Math.random() - 0.5) * 0.4); };
player.glider.onRocketEnd = (n) => { if (n > 0) fireworkExplode(rockets.host, player.position.x, player.position.y + 0.9, player.position.z, Array(n).fill(0xffffff)); };
const tickSims = (): void => { fluids.tick(); fires.tick(); tnt.tick(); falling.tick(); crops.tick(); lava.tick(); redstone.tick(); weatherFx.tick(); };
const sky = new Sky(env);
if (save.time !== null) sky.time = save.time;
weather.load(save.weather, WORLD_SEED);

// --- UI foundation ------------------------------------------------------------
await loadKit();
// vanilla item sprites before any icon is drawn
await loadItemIcons();

let mode: GameMode = save.gameMode;
const inventory = new Inventory();
if (save.inventory) {
  inventory.deserialize(save.inventory);
} else if (save.hotbar) {
  // a pre-survival world: its hotbar becomes the first row of the inventory
  save.hotbar.ids.forEach((id, i) => {
    if (i < HOTBAR && id > 0) setStack(inventory.slots[i], stackOf(id, Math.max(1, save.hotbar!.counts[i] | 0)));
  });
} else if (mode === 'creative') {
  DEFAULT_HOTBAR.slice(0, HOTBAR).forEach((id, i) => { if (id > 0) setStack(inventory.slots[i], stackOf(id)); });
  setStack(inventory.slots[7], stackOf(I.WATER_BUCKET));
}

const furnaces = new FurnaceWorld();
furnaces.load(save.furnaces);
// brewing stands ride in the stats blob (no save-format change): keys are "<dimension>:x,y,z"
const brewing = new BrewingWorld();
const brewingRaw: Record<string, number[]> = { ...((save.stats as { brewing?: Record<string, number[]> } | null)?.brewing ?? {}) };
brewing.load(brewingRaw, save.dimension);
/** Fold the current dimension's stands into the saved blob. */
function brewingBlob(): Record<string, number[]> {
  const pre = save.dimension + ':';
  for (const k of Object.keys(brewingRaw)) if (k.startsWith(pre)) delete brewingRaw[k];
  Object.assign(brewingRaw, brewing.serialize(save.dimension));
  return brewingRaw;
}
const statsBlob = (): Record<string, unknown> => ({ stats: stats.serialize(), selected: inventory.selected, enchantSeed, brewing: brewingBlob(), minecarts: minecarts.serialize() });

const armorOf = (): { defense: number; toughness: number } => {
  let defense = 0, toughness = 0;
  for (const s of inventory.armor) {
    const a = isEmpty(s) ? undefined : itemDef(s.id).armor;
    if (a) { defense += a.defense; toughness += a.toughness; }
  }
  return { defense, toughness };
};

const icons = new ItemIcons(atlas);
const hudStage = new GuiStage(document.body, 10);
hudStage.setGuiScaleSetting(settings.guiScale);
const hud = new GameHud(hudStage, icons, inventory);

// What hurt or killed the player, in words (agent clients read it; see src/agent/agentApi.ts).
const DAMAGE_WORDS: Record<string, string> = {
  fall: 'a fall', drown: 'drowning', starve: 'starvation', void: 'falling out of the world', fire: 'fire', lava: 'lava',
  explosion: 'an explosion', cactus: 'a cactus', projectile: 'a projectile', player: 'another player', melee: 'a mob',
  magic: 'magic', generic: 'something',
};
function damageCause(src: DamageSource): string {
  if (src.kind === 'player') {
    // a player's hit carries where it came from: name whoever stands there
    let who = '', best = 9;
    for (const r of net.session?.remotePlayers() ?? []) {
      const d = Math.hypot(r.state.x - (src.x ?? 1e9), r.state.z - (src.z ?? 1e9));
      if (d < best) { best = d; who = r.name; }
    }
    return who ? `the player ${who} hitting you` : 'another player hitting you';
  }
  const m = src.attackerId ? entities.mobs.find((e) => e.id === src.attackerId) : undefined;
  return m ? `a ${m.type}` : DAMAGE_WORDS[src.kind] ?? src.kind;
}
const agentNotes: string[] = [];
function agentNote(text: string): void {
  if (!(window as unknown as { __agent?: boolean }).__agent) return;
  agentNotes.push(text);
  if (agentNotes.length > 20) agentNotes.shift();
}

// --- player stats -----------------------------------------------------------
const stats = new PlayerStats({
  getArmor: armorOf,
  onHurt: (amount, src) => {
    agentNote(`lost ${Math.round(amount * 10) / 10} health to ${damageCause(src)}`);
    player.hurt((src.x ?? player.position.x) - player.position.x, (src.z ?? player.position.z) - player.position.z);
    // armor wears when it absorbs a hit (vanilla: max(1, damage / 4) per piece)
    if (src.kind !== 'fall' && src.kind !== 'drown' && src.kind !== 'starve' && src.kind !== 'void' && src.kind !== 'generic') {
      const wear = Math.max(1, Math.floor(amount / 4));
      for (const s of inventory.armor) if (!isEmpty(s)) wearStack(inventory, s, wear);
    }
  },
  onDeath: (src) => {
    const p = player.position;
    agentNote(`YOU DIED: killed by ${damageCause(src)} at ${Math.floor(p.x)} ${Math.floor(p.y)} ${Math.floor(p.z)}; your items dropped there`);
    onPlayerDeath();
  },
  tryTotem: () => {
    if (mode !== 'survival' || !popTotem([inventory.held, inventory.offhand])) return false;
    inventory.changed();
    hud.toast('Totem of Undying!');
    const e = player.camera.position;
    for (let i = 0; i < 30; i++) hearts.spawn(e.x + (Math.random() - 0.5), e.y - 0.5 + Math.random(), e.z + (Math.random() - 0.5), 'happy');
    sfx.play('item.totem.use');
    return true;
  },
  getEnchantProtection: (kind) => armorEPF(inventory, kind),
});
const savedStats = save.stats as { stats?: unknown; selected?: number } | null;
if (savedStats?.stats) stats.load(savedStats.stats as never);
if (typeof savedStats?.selected === 'number') inventory.selected = Math.max(0, Math.min(HOTBAR - 1, savedStats.selected | 0));
if (mode === 'survival' && stats.dead) stats.respawn();

player.onFall = (d) => {
  stats.fall(d);
  const n = landingDustCount(d);
  if (n > 0) particles.dust(player.position.x, Math.floor(player.position.y - 0.2) + 1, player.position.z, chunks.world.getBlock(Math.floor(player.position.x), Math.floor(player.position.y - 0.2), Math.floor(player.position.z)), n, 1.5);
};
player.baseFov = settings.fov;
player.sensitivity = settings.mouseMultiplier;
player.viewBobbing = settings.viewBobbing;
player.autoJump = settings.autoJump;

function applyMode(m: GameMode): void {
  mode = m;
  save.setGameMode(m);
  player.survival = m === 'survival';
  if (m === 'survival') { player.flying = false; if (player.spectator) player.setSpectator(false); player.flying = false; }
  menus.setGameMode(m);
}

// --- items, xp, mobs ----------------------------------------------------------
/** Brightness for dropped items / the held item: sky light dims at night but keeps a moonlit floor. */
const lightAt = (x: number, y: number, z: number): number => {
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  // night sky light is ~1/5 of the day's (the terrain shader's ambient ratio), so drops match the ground
  const s = chunks.world.getSky(bx, by, bz) * (0.22 + 0.78 * env.uDayLight.value);
  return Math.max(s, chunks.world.getBlockLight(bx, by, bz)) / 15;
};

/** Lightmap inputs at the camera eye for the held item: sky (time-of-day scaled) and block light, 0..1. */
const handLight = (): { sky: number; block: number } => {
  const c = player.camera.position;
  const bx = Math.floor(c.x), by = Math.floor(c.y), bz = Math.floor(c.z);
  return {
    sky: chunks.world.getSky(bx, by, bz) * (0.22 + 0.78 * env.uDayLight.value) / 15,
    block: chunks.world.getBlockLight(bx, by, bz) / 15,
  };
};

const items = new ItemEntityManager();
const xpOrbs = new XpOrbManager();
const playerBox = {
  get x() { return player.position.x; }, get y() { return player.position.y; }, get z() { return player.position.z; },
  width: 0.6, height: 1.8, eyeHeight: 1.62,
  get alive() { return !stats.dead; }, get spectator() { return player.spectator; },
};

/** A creeper's own blast hurts through the entity manager; primed TNT's is applied in onExplode. */
let creeperBlast = false;
const playerTarget: EntityHost['player'] = {
  get x() { return player.position.x; }, get y() { return player.position.y; }, get z() { return player.position.z; },
  get eyeY() { return player.position.y + 1.62; }, width: 0.6, height: 1.8,
  get alive() { return !stats.dead; },
  get creative() { return mode === 'creative'; },
  get spectator() { return player.spectator; },
  damage: (amount, src) => {
    if (tryBlock(amount, src)) return 'blocked';
    const dealt = stats.damage(amount, src);
    // wither skeleton / cave spider / husk hits and stray arrows leave their effect (game/effects/sources.ts)
    if (dealt > 0 && src.attackerId !== undefined) {
      const m = entities.mobs.find((x) => x.id === src.attackerId);
      if (m) {
        if (src.kind === 'projectile') {
          if ((m.type as string) === 'stray') stats.effects.add(STRAY_ARROW_EFFECT.id, STRAY_ARROW_EFFECT.amp, STRAY_ARROW_EFFECT.dur);
        } else for (const f of mobHitEffects(m.type)) stats.effects.add(f.id, f.amp, f.dur);
      }
    }
    // Thorns: hit back the mob that struck us in melee
    if (dealt > 0 && src.kind === 'melee' && src.attackerId !== undefined && mode === 'survival') {
      const t = thornsDamage(inventory);
      const m = t > 0 ? entities.mobs.find((x) => x.id === src.attackerId) : undefined;
      if (m) entities.attack(m, t, player.position.x, player.position.z, 0);
    }
  },
  // a blocked hit does not shove the player; netherite armor resists knockback
  knockback: (dx, dz, strength) => {
    if (blockedTick !== tickCounter) player.knockback(dx, dz, strength * (1 - knockbackResistance(inventory)));
  },
  setOnFire: (ticks) => stats.setOnFire(ticks),
};
const entityHost: EntityHost = {
  world: chunks.world,
  isNight: () => env.uDayLight.value < 0.3,
  dayTime: () => sky.time * 24000,
  sunLevel: () => env.uDayLight.value,
  player: playerTarget,
  explode: (x, y, z, power) => {
    creeperBlast = true;
    try { tnt.blast(x, y, z, power); } finally { creeperBlast = false; }
  },
  dropItem: (s, x, y, z, vx, vy, vz) => { items.spawn(s, x, y, z, vx, vy, vz); },
  dropXp: (amount, x, y, z) => xpOrbs.spawn(x, y, z, amount),
  trample: (x, y, z) => trampleAt(x, y, z),
  difficulty: () => currentDifficulty(),
  isRainingAt: (x, y, z) => weather.isRainingAt(x, y, z),
  restTicks: () => stats.timeSinceRest,
  heldItem: () => (isEmpty(inventory.held) ? 0 : inventory.held.id),
  detectScale: () => visibilityScale(stats.effects, inventory.armor.reduce((n, a) => n + (isEmpty(a) ? 0 : 1), 0)),
  // door being hacked down by a zombie: the block-crack overlay, one stage per call
  crackDoor: (x, y, z, stage) => { if (stage < 0) doorCrack.clear(); else doorCrack.set(x, y, z, (stage + 0.5) / 10); },
  // doors opened / closed / broken by villagers and zombies (entities/nav)
  setBlock: (x, y, z, id) => {
    if (!chunks.canEdit(x, y, z)) return;
    const was = DOOR_STATE[chunks.world.getBlock(x, y, z)];
    chunks.setBlock(x, y, z, id);
    if (was && !was.top && was.open !== (DOOR_STATE[id]?.open ?? false)) sfx.door(x, y, z, DOOR_STATE[id]?.open ?? false);
  },
  eatGrass: (x, y, z) => {
    if (chunks.world.getBlock(x, y, z) !== GRASS_ID || !chunks.canEdit(x, y, z)) return false;
    chunks.setBlock(x, y, z, DIRT_ID);
    return true;
  },
  effect: (kind, x, y, z) => {
    if (kind === 'heart') { hearts.spawn(x, y, z); return; }
    // a removed corpse puffs into a few smoke wisps (vanilla's death cloud); a
    // creeper's blast already throws the real explosion via the TNT sim
    if (kind === 'poof') {
      for (let i = 0; i < 8; i++) {
        particles.fuse(x + (Math.random() - 0.5) * 0.8, y + Math.random() * 1.2, z + (Math.random() - 0.5) * 0.8);
      }
    }
  },
  rnd: Math.random,
};
const entities = new EntityManager(entityHost);
// warden: sounds, sonic boom beam, Darkness and the boom's lift reach the local player here (remote peers get the damage only)
wardenHooks.sound = (id, x, y, z) => sfx.play(id, { x, y, z });
wardenHooks.darkness = (p, ticks) => { if (p.id === '') stats.effects.add(FX.DARKNESS, 0, ticks); };
wardenHooks.toss = (p, vy) => { if (p.id === '') player.velocity.y += vy; };
wardenHooks.fx.boom = (x0, y0, z0, x1, y1, z1) => {
  const n = Math.max(2, Math.floor(Math.hypot(x1 - x0, y1 - y0, z1 - z0) / 0.5));
  for (let i = 1; i <= n; i++) { const t = i / n; particles.spark(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, z0 + (z1 - z0) * t, 0, 0, 0, 0.12, 0.5, 0.55); }
};
// overworld spawn tables never run in the nether (nether mobs: entities/nether)
entities.spawningEnabled = chunks.dimension === 'overworld';
netherHooks.spawn = (t, x, y, z) => entities.spawnMob(t, x, y, z);
// biome-gated spawns (husk / stray / swamp slime) read the biome at a column
overworld2Hooks.biomeAt = (x, z) => BIOME_NAMES[hudTerrain.columnInfo(Math.floor(x), Math.floor(z)).biome as Biome].toLowerCase();
// piglins stay calm toward a player wearing any piece of gold armour
witchHooks.throwPotion = (x, y, z, vx, vy, vz, item, dmg) => {
  const e = potions.throwPotion(x, y, z, 0, 0, item, dmg);
  e.vx = vx; e.vy = vy; e.vz = vz;
};
witchHooks.playerHasEffect = (id) => stats.effects.has(id);
piglinHooks.findGold = (x, y, z, range) => {
  let best: { x: number; y: number; z: number; take(): void } | null = null, bd = range * range;
  for (const e of items.items) {
    if (e.removed || e.stack.id !== I.GOLD_INGOT || e.collect > 0) continue;
    const d = (e.x - x) ** 2 + (e.y - y) ** 2 + (e.z - z) ** 2;
    if (d < bd) { bd = d; best = { x: e.x, y: e.y, z: e.z, take: () => { e.stack.count--; } }; }
  }
  return best;
};
const isGoldArmor = (id: number): boolean => id === I.GOLDEN_HELMET || id === I.GOLDEN_CHESTPLATE || id === I.GOLDEN_LEGGINGS || id === I.GOLDEN_BOOTS;
// a remote player (multiplayer host) is judged by the armour ids it replicates, the local one by the inventory
piglinHooks.wearsGold = (t) => (t?.id ? (t.armor ?? []).some(isGoldArmor) : inventory.armor.some((s) => !isEmpty(s) && isGoldArmor(s.id)));
piglinHooks.canSeePlayer = (p) => lineClear(chunks.world, p.x, p.y + 1.79, p.z, player.position.x, player.position.y + 1.62, player.position.z);
const netherSpawner = new NetherSpawner({
  world: chunks.world,
  rnd: Math.random,
  player: () => player.position,
  biomeAt: (x, z) => netherGen.biomeAt(x, z),
  inFortress: (x, y, z) => inFortress(WORLD_SEED, x, y, z),
  inBastion: (x, y, z) => inBastion(WORLD_SEED, x, y, z),
  spawn: (t, x, y, z) => entities.spawnMob(t, x, y, z),
  count: () => entities.mobs.reduce((n, m) => n + (!m.dying && isHostileType(m.type) ? 1 : 0), 0),
  countType: (t) => entities.mobs.reduce((n, m) => n + (!m.dying && m.type === t ? 1 : 0), 0),
});

// --- scene ----------------------------------------------------------------------
let debugOn = false;
const prefs = loadPrefs();

// --- perf HUD (FPS viewer; Shift+3 cycles compact -> full -> off) --------------
const perf = new PerfHud(renderer, document.body);
// restore the persisted debug state (Shift+3) at startup
if (prefs.debug) {
  debugOn = true;
  perf.setMode(PerfMode.Full);
  hud.toggleDebug();
}
perf.detailLines = () => {
  const r = renderer.info.render;
  const m = renderer.info.memory;
  const st = chunks.stats;
  return [
    `draws ${r.calls}   tris ${(r.triangles / 1000).toFixed(0)}k   geo ${m.geometries}`,
    `chunks ${st.ready}/${st.wanted} ready   ${st.chunks} loaded`,
    `queue gen ${st.pendingGen}  mesh ${st.pendingMesh}  upload ${st.pendingUploads}`,
    `worker gen ${st.genMs.toFixed(1)} ms   mesh ${st.meshMs.toFixed(1)} ms   world ready ${(worldReadyMs / 1000).toFixed(1)} s`,
    `${quality.label}   view ${quality.viewRadius}   scale ${renderer.getPixelRatio().toFixed(2)}   blend ${quality.biomeBlend === 0 ? 'off' : (quality.biomeBlend + 1) * 2 - 1 + 'x' + ((quality.biomeBlend + 1) * 2 - 1)}   shadow passes ${shadows.lastRenders}`,
  ];
};
perf.errorLines = () => {
  const out = errLines.slice();
  for (const p of renderer.info.programs ?? []) {
    const diag = (p as unknown as { diagnostics?: { failedShader?: boolean; log?: string } }).diagnostics;
    if (diag && (diag.failedShader || diag.log)) out.push('PROG ' + String(diag.log).slice(0, 300));
  }
  return out;
};

// lightweight terrain query for the debug HUD
const hudTerrain = new TerrainGenerator(WORLD_SEED);

// --- villages (entities/village): villagers, iron golems, pillager outposts ---
// VillageSystem was fully implemented but never constructed, so generated
// villages stayed empty. Every dep is a closure, so consts declared later (sfx,
// lockOrPause) resolve when the hook is first called, not at construction.
const tradeScreen = new VillagerTradeScreen(document.body, {
  inventory,
  addXp: (n) => stats.addXp(n),
  nameOf,
  iconFor: (id) => icons.get(id),
  playSound: (id) => sfx.play(id),
  // server mode: the trade is an intent; goods, stock and XP come back as `slots` / `offers` / `stats`
  remote: srv ? (i) => { srv.send({ t: 'trade', win: srvTradeWin, i }); } : undefined,
});
let srvTradeWin = 0;
// --- riding (game/mounts.ts): horse screen + jump bar, rider input comes from Player.rideInput ---
const horseScreen = new HorseScreen(document.body, {
  heldId: () => (isEmpty(inventory.held) ? 0 : inventory.held.id),
  consumeHeld: (n) => { if (mode === 'survival') inventory.consumeHeld(n); },
  give: (id) => { if (inventory.add(id) > 0) dropStack(stackOf(id)); },
  nameOf,
  iconFor: (id) => icons.get(id),
});
const rideHud = new RideHud(hudStage);
mountState.onMessage = (m) => hud.toast(window.matchMedia?.('(pointer: coarse)').matches ? 'Tap SNEAK to dismount' : m);
initMounts(player, chunks.world);
// minecarts and rails (entities/vehicles/minecart*.ts, world/rails.ts); carts ride in the stats blob
const minecarts = new MinecartSystem({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  dimension: () => chunks.dimension,
  creative: () => mode === 'creative',
  player,
  drop: (s, x, y, z) => { items.spawn(s, x, y, z, (Math.random() - 0.5) * 0.1, 0.15, (Math.random() - 0.5) * 0.1); },
  consumeHeld: (n) => inventory.consumeHeld(n),
  mustDismount: () => stats.dead || travel.travelling,
  toast: (m) => hud.toast(m),
});
minecarts.load((save.stats as { minecarts?: unknown } | null)?.minecarts);
const minecartView = new MinecartRenderer(env);
const mountEnv: MountEnv = {
  get sneaking() { return player.sneaking; },
  consumeHeld: (n) => { if (mode === 'survival') inventory.consumeHeld(n); },
  openHorse: (h) => horseScreen.show(h, () => lockOrPause()),
  effect: (k, x, y, z) => entityHost.effect(k, x, y, z),
};
const village = new VillageSystem({
  entities,
  host: entityHost,
  setBlock: (x, y, z, id) => chunks.setBlock(x, y, z, id),
  structures: (x, z) => hudTerrain.nearestStructures(x, z, 8),
  biomeName: (x, z) => BIOME_NAMES[hudTerrain.columnInfo(Math.floor(x), Math.floor(z)).biome as Biome],
  // populating a village/outpost is remembered in the save, like vanilla
  flag: { has: (k) => save.hasFlag(k), set: (k) => save.setFlag(k) },
  sound: (id, x, y, z) => sfx.play(id, { x, y, z }),
  toss: (vy) => { player.velocity.y += vy; },
  dimension: () => chunks.dimension,
  creative: () => mode === 'creative',
  consumeHeld: (n) => inventory.consumeHeld(n),
  openTrade: (v, onClose) => tradeScreen.openTrade(v, () => { onClose(); lockOrPause(); }),
  // no difficulty UI yet: read from localStorage (see currentDifficulty)
  difficulty: () => (currentDifficulty() === 'peaceful' ? 'easy' : (currentDifficulty() as 'easy' | 'normal' | 'hard')),
});
// --- wandering trader + llamas (entities/trader): daily spawn roll, trading, llama chest, caravans, spit ---
const llamaScreen = new LlamaScreen(document.body, {
  held: () => inventory.held,
  takeHeld: (n) => { if (mode === 'survival') inventory.consumeHeld(n); },
  give: (s) => { const left = inventory.add(s.id, s.count, s.dmg, s.ench); if (left > 0) dropStack({ ...s, count: left }); },
  nameOf,
  iconFor: (id) => icons.get(id),
  maxStack: maxStackOf,
});
const traders = new TraderSystem({
  entities,
  host: entityHost,
  dimension: () => chunks.dimension,
  creative: () => mode === 'creative',
  consumeHeld: (n) => inventory.consumeHeld(n),
  openTrade: (t, onClose) => tradeScreen.openTrade(t, () => { onClose(); lockOrPause(); }),
  openLlama: (l, onClose) => llamaScreen.show(l, () => { onClose(); lockOrPause(); }),
  sound: (id, x, y, z) => sfx.play(id, { x, y, z }),
  // the spawn roll rides in a world flag "trader:<ticks to the next roll>:<chance in permille>"
  state: {
    load: () => {
      for (const f of save.flags) if (f.startsWith('trader:')) { const [, t, c] = f.split(':'); return [Number(t), Number(c)]; }
      return null;
    },
    save: (v) => {
      for (const f of [...save.flags]) if (f.startsWith('trader:')) save.flags.delete(f);
      save.setFlag(`trader:${v[0]}:${v[1]}`);
    },
  },
});

const scene = new THREE.Scene();
scene.add(chunks.group);
scene.add(sky.group);
const dimEnv = new DimensionEnv(env, sky.group, scene);
// the camera lives in the scene so the first-person hand parented to it renders
scene.add(player.camera);

// Sun shadow casters live in their own scene (depth-only twins of the chunk
// meshes); nothing in it moves, so skip the per-render matrix walk.
const shadowScene = new THREE.Scene();
shadowScene.add(chunks.shadowGroup);
shadowScene.matrixWorldAutoUpdate = false;

/** Distance at which terrain is fully fogged: just inside the meshed disc. */
function fogFar(): number {
  return (quality.viewRadius - 0.5) * CHUNK_SIZE;
}

function resize(): void {
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.pixelRatioCap));
  renderer.setSize(window.innerWidth, window.innerHeight);
  const size = renderer.getDrawingBufferSize(new THREE.Vector2());
  post.setSize(size.x, size.y);
  player.camera.aspect = window.innerWidth / window.innerHeight;
  player.camera.updateProjectionMatrix();
}

/** Push the current graphics + render-distance options to every consumer. */
function applyQuality(): void {
  quality = presetOf();
  voxel.setQuality(quality);
  shadows.setOptions({ cascades: quality.shadowCascades, mapSize: quality.shadowMapSize });
  post.setOptions({ bloom: quality.bloom, msaa: quality.msaa });
  chunks.setViewRadius(quality.viewRadius);
  setBlendRadius(quality.biomeBlend);
  chunks.broadcastConfig({ biomeBlend: quality.biomeBlend });
  resize();
}

const GRAPHICS_ORDER = ['fast', 'fancy', 'ultra'] as const;
/** G: cycle the graphics preset (Fast / Fancy / Ultra). */
function cycleQuality(): void {
  const next = GRAPHICS_ORDER[(GRAPHICS_ORDER.indexOf(settings.graphics) + 1) % GRAPHICS_ORDER.length];
  settings.set('graphics', next);
  hud.toast(`Graphics: ${PRESETS[next].label}`);
}

settings.onChange((key) => {
  switch (key) {
    case 'fov': player.baseFov = settings.fov; break;
    case 'sensitivity': player.sensitivity = settings.mouseMultiplier; break;
    case 'viewBobbing': player.viewBobbing = settings.viewBobbing; break;
    case 'autoJump': player.autoJump = settings.autoJump; break;
    case 'brightness': env.uGamma.value = settings.brightness; break;
    case 'guiScale': hudStage.setGuiScaleSetting(settings.guiScale); break;
    case 'renderDistance':
    case 'graphics': applyQuality(); break;
    default: break;
  }
});

// block highlight wireframe
const highlight = new BlockOutline();
scene.add(highlight.object3d);

// block-breaking particles, primed-TNT smoke, blast clouds
const particles = new BlockParticles(chunks.world, atlas.canvas, particleSprites);
particles.env = env;
scene.add(particles.points);
const hearts = new Hearts();
scene.add(hearts.group);
// farming: crop growth (world/crops.ts) and thrown eggs (entities/thrown.ts)
const crops = new CropSim({ world: chunks.world, canEdit: (x, y, z) => chunks.canEdit(x, y, z), setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); } });
const thrown = new ThrownSim();
const thrownRenderer = new ThrownRenderer();
scene.add(thrownRenderer.group);
const thrownCtx = {
  get world() { return chunks.world; },
  raycastMob: (ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, d: number) => entities.raycast(ox, oy, oz, dx, dy, dz, d),
  hitMob: (m: Mob, fx: number, fz: number) => { entities.attack(m, 0, fx, fz, 0.4); },
  spawnChick: (x: number, y: number, z: number) => { spawnBaby({ spawn: (t, a, b, c) => entities.spawnMob(t, a, b, c) }, 'chicken', x, y, z); },
  shatter: (x: number, y: number, z: number) => thrownRenderer.shatter(x, y, z),
  loaded: (x: number, z: number) => chunks.canEdit(Math.floor(x), 0, Math.floor(z)),
};

// fishing bobber (entities/bobber.ts): cast / reel with a rod, one per player
const bobbers = new BobberSim();
const bobberRenderer = new BobberRenderer();
scene.add(bobberRenderer.group);
const bobberCtx: BobberCtx = {
  get world() { return chunks.world; },
  player: () => ({ x: player.position.x, y: player.position.y, z: player.position.z, alive: !stats.dead && !player.spectator }),
  holdingRod: () => !isEmpty(inventory.held) && inventory.held.id === I.FISHING_ROD,
  raining: (x, y, z) => weather.isRainingAt(x, y, z),
  skyVisible: (x, y, z) => columnTop(chunks.world, Math.floor(x), Math.floor(z)) <= y,
  raycastMob: thrownCtx.raycastMob,
  hitMob: (m, fx, fz) => { entities.attack(m, 0, fx, fz, 0.4); },
  itemAt: (x, y, z, r) => {
    for (const e of items.items) {
      if (e.collect > 0 || Math.abs(e.x - x) > 0.125 + r || Math.abs(e.z - z) > 0.125 + r || y < e.y - r || y > e.y + 0.25 + r) continue;
      return { target: e, gone: () => e.collect > 0 || !items.items.includes(e) };
    }
    return null;
  },
  spawnItem: (s, x, y, z, vx, vy, vz) => { items.spawn(s, x, y, z, vx, vy, vz); },
  spawnXp: (amount, x, y, z) => xpOrbs.spawn(x, y, z, amount),
  fx: (k, x, y, z) => {
    bobberRenderer.fx(k, x, y, z);
    if (k === 'splash' || k === 'bite') sfx.play('entity.generic.splash', { x, y, z });
    else if (k === 'cast') sfx.play('entity.arrow.shoot', { x, y, z });
    else if (k === 'catch') sfx.play('entity.item.pickup', { x, y, z });
  },
  loaded: thrownCtx.loaded,
};
/** Right-click with a rod: cast, or reel in (rod wear per rodDamage, Unbreaking applies). */
function useRod(): void {
  const held = inventory.held;
  if (bobbers.hook) {
    const r = bobbers.reel(bobberCtx);
    if (r.damage > 0 && mode === 'survival' && wearStack(inventory, held, r.damage)) hud.toast(`Your ${nameOf(I.FISHING_ROD)} broke`);
    player.startSwing();
    return;
  }
  const p = player.position;
  bobbers.cast(bobberCtx, p.x, p.y + 1.52, p.z, player.yaw, player.pitch, { lure: enchLevel(held, ENCH.LURE), luck: enchLevel(held, ENCH.LUCK_OF_THE_SEA) });
  player.startSwing();
}
/** Where the line leaves the rod: in front of the right hand (first person: view space; third person: beside the body). */
const rodTipPos = new THREE.Vector3();
function rodTip(): THREE.Vector3 {
  if (player.isFirstPerson) return rodTipPos.set(0.45, -0.08, -0.9).applyQuaternion(player.camera.quaternion).add(player.camera.position);
  const p = player.position, y = player.yaw;
  return rodTipPos.set(p.x + Math.cos(y) * 0.4 - Math.sin(y) * 0.8, p.y + 1.25, p.z - Math.sin(y) * 0.4 - Math.cos(y) * 0.8);
}

// splash / lingering potions (game/brewing/splash.ts)
const potions = new PotionSim();
const potionRenderer = new PotionRenderer();
scene.add(potionRenderer.group);
const potionCtx: SplashCtx = {
  get world() { return chunks.world; },
  raycastMob: thrownCtx.raycastMob,
  mobsNear: (x, y, z, r) => entities.mobs.filter((m) => m.alive && Math.abs(m.x - x) <= r && Math.abs(m.z - z) <= r && Math.abs(m.y - y) <= r),
  player: () => ({ x: player.position.x, y: player.position.y, z: player.position.z, ok: !stats.dead && !player.spectator }),
  applyToMob: (m, list, k) => { for (const e of list) giveMobEffect(m, e.id, e.amp, e.dur, k); },
  applyToPlayer: (list, k) => applyEffectList(stats.effects, stats.effectTarget, list, k),
  shatter: (x, y, z, c, l) => potionRenderer.shatter(x, y, z, c, l),
  loaded: thrownCtx.loaded,
};
const fxHud = new EffectsHud(document.body);

// a blast throws a smoke cloud (vanilla's huge explosion emitter) and hurts what
// is near it; the sim is DOM-free, so the effects are hooked up here
tnt.onExplode = (x, y, z, power) => {
  particles.explosion(x, y, z, power);
  if (!creeperBlast) entities.damageInRadius(x, y, z, 8, 4, true);
};

// primed TNT is an entity, so it needs its own cube mesh (see render/primedTnt.ts)
const primedTnt = new PrimedTntRenderer(voxel.opaque, chunks.world);
scene.add(primedTnt.group);
const fallingView = new FallingBlockRenderer(voxel.opaque, chunks.world);
scene.add(fallingView.group);

// falling petals / leaves shed by tree canopies
const ambient = new AmbientParticles(chunks.world, env);
scene.add(ambient.points);

// weather: biome sky / fog, rain and snow, lightning, Nether ash and spores
// (game/weather*.ts, render/weather.ts, render/biomeAmbience.ts)
const biomeOf = (x: number, z: number): Biome => hudTerrain.columnInfo(x, z).biome;
weather.world = { topY: (x, z) => columnTop(chunks.world, x, z), biomeAt: biomeOf };
const weatherRenderer = new WeatherRenderer(chunks.world, env, biomeOf);
scene.add(weatherRenderer.group);
const envAmbience = new EnvAmbience(env, sky.group, biomeOf);
const netherParticles = new NetherParticles();
scene.add(netherParticles.points);
const weatherFx = new WeatherEffects({
  world: chunks.world,
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  focus: () => player.position,
  strike: (x, y, z) => {
    const c = player.camera.position;
    weatherRenderer.strike(x, y, z, c.x, c.y, c.z);
    for (const m of entities.mobs.slice()) {
      if (m.removed || Math.hypot(m.x - x - 0.5, m.z - z - 0.5) > BOLT_RADIUS || Math.abs(m.y - y) > BOLT_RADIUS + 2) continue;
      const to = lightningConversion(m.type);
      if (to) { m.removed = true; entities.spawnMob(to, m.x, m.y, m.z); }
      else entities.attack(m, BOLT_DAMAGE, x + 0.5, z + 0.5, 0.4, { fireTicks: 160 });
    }
    const pp = player.position;
    if (mode === 'survival' && !stats.dead && Math.hypot(pp.x - x - 0.5, pp.z - z - 0.5) <= BOLT_RADIUS && Math.abs(pp.y - y) <= BOLT_RADIUS + 2) {
      playerTarget.damage(BOLT_DAMAGE, { kind: 'generic', x: x + 0.5, y, z: z + 0.5 });
      stats.setOnFire(160);
    }
  },
});

// mobs, dropped items, XP orbs, the player's own body, the first-person hand
const entityRenderer = new EntityRenderer(env);
ENTITY_LIGHT.sample = (x, y, z) => {
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  return [chunks.world.getSky(bx, by, bz), chunks.world.getBlockLight(bx, by, bz)];
};
scene.add(entityRenderer.group);
const netherRt = new NetherRuntime({
  chunks, entities, env, scene, camera: player.camera,
  player: { position: player.position, velocity: player.velocity, alive: () => !stats.dead },
  hurtPlayer: (amount, x, _y, z, fire) => {
    if (mode !== 'survival') return;
    // small (blaze) fireballs are projectiles a shield stops (vanilla); ghast balls hit as explosions
    const r = playerTarget.damage(amount, { kind: fire > 0 ? 'projectile' : 'explosion', x, z });
    if (fire > 0 && r !== 'blocked') stats.setOnFire(fire);
  },
  explode: (x, y, z, power) => {
    entityHost.explode(x, y, z, power);
    entities.damageInRadius(x, y, z, power * 2, power, true);
  },
  portalParticle: (id, x, y, z, tx, ty, tz) => particles.portal(id, x, y, z, tx, ty, tz),
  pearlGateway: (x, y, z) => !stats.dead && chunks.dimension === 'end' && gateways.enterAt(x, y, z),
  pearlTeleport: (x, y, z) => {
    if (stats.dead) return;
    player.teleportTo(x, y, z);
    if (mode === 'survival') playerTarget.damage(5, { kind: 'fall' });
    // vanilla: 5% of pearl landings hatch an endermite
    if (Math.random() < 0.05) entities.spawnMob('endermite', x, y, z);
  },
});
// structure blocks that act: trial spawners, vaults, the silverfish spawner (game/trials.ts)
const trials = new TrialRuntime({
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  player: () => ({ x: player.position.x, y: player.position.y, z: player.position.z, alive: !stats.dead && !player.spectator }),
  spawnMob: (t, x, y, z) => entities.spawnMob(t, x, y, z),
  countNear: (t, x, y, z, r) => entities.mobs.reduce((n, m) => n + (m.alive && m.type === t && Math.abs(m.x - x) <= r && Math.abs(m.z - z) <= r && Math.abs(m.y - y) <= r ? 1 : 0), 0),
  eject: (s, x, y, z) => { items.spawn(s, x, y, z); },
  rnd: Math.random,
  saveCooldown: (key, at) => { save.setFlag(`trial_cd:${key}:${Math.floor(at)}`); },
});
trials.restoreFlags(save.flags);
// elytra: wear 1 per second of gliding, hurt on wall crashes (player/elytra.ts)
const elytraStack = (): Stack | null => { const s = inventory.armor[1]; return !isEmpty(s) && s.id === I.ELYTRA ? s : null; };
player.glider.onWear = () => {
  const s = elytraStack();
  if (s && s.dmg < itemDef(s.id).maxDamage! - 2) inventory.damage(s, 1);
};
player.onGlideCrash = (d) => { if (mode === 'survival') playerTarget.damage(d, { kind: 'fall' }); };
// chorus fruit hops and End Gateway warps (game/endOuter.ts)
const outerHost = {
  getBlock: (x: number, y: number, z: number) => chunks.world.getBlock(x, y, z),
  teleport: (x: number, y: number, z: number) => { player.teleportTo(x, y, z); },
  warp: (x: number, z: number) => { pending = { x, z }; player.position.set(x, WORLD_HEIGHT - 2, z); player.setFrozen(true); },
  seed: WORLD_SEED,
  rnd: Math.random,
};
const gateways = new GatewayRuntime(outerHost);
const endRt = new EndRuntime({
  env, scene, camera: player.camera, seed: WORLD_SEED,
  dimension: () => chunks.dimension,
  dropEye: (x, y, z) => { items.spawn(stackOf(I.EYE_OF_ENDER), x, y, z, 0, 0, 0, 10); },
  burst: (x, y, z) => particles.burst(Math.floor(x), Math.floor(y), Math.floor(z), END_PORTAL_FRAME_EYE_ID),
});
// ender dragon fight (entities/end, render/entities/dragon): glue lives in game/endFightGlue.ts
const endFight: EndFightGlue = new EndFightGlue({
  chunks, entities, env, scene, camera: player.camera, stage: hudStage, player: playerTarget, save,
  explode: (x, y, z, power) => entityHost.explode(x, y, z, power),
  dropXp: (amount, x, y, z) => xpOrbs.spawn(x, y, z, amount),
  particles, sfx: { play: (id, pos) => sfx.play(id, pos) },
  meleeDamage: () => {
    const held = inventory.held;
    return meleeHit({
      held: isEmpty(held) ? null : held, charge: attackCharge(), targetType: 'zombie', targetWet: false,
      falling: player.velocity.y < 0 && !player.flying, onGround: player.onGround, inWater: player.inWater,
      sprinting: player.sprinting, fallDistance: player.fallen,
    }).damage;
  },
  afterMelee: () => {
    attackTicks = 0;
    if (mode !== 'survival') return;
    stats.addExhaustion(EXHAUST_ATTACK);
    const held = inventory.held;
    const tool = isEmpty(held) ? undefined : itemDef(held.id).tool;
    if (tool && wearStack(inventory, held, tool.type === 'sword' || tool.type === 'mace' || tool.type === 'trident' || tool.type === 'spear' ? 1 : 2)) hud.toast(`Your ${nameOf(held.id)} broke`);
  },
  tickCount: () => tickCounter,
  *projectiles() {
    for (const e of thrown.eggs) if (!e.dead) yield { x: e.x, y: e.y, z: e.z, px: e.px, py: e.py, pz: e.pz, damage: 0, hit: () => { e.dead = true; } };
    for (const t of tridents.list) {
      if (t.removed || t.stuck || t.dealt || t.returning) continue;
      yield {
        x: t.x, y: t.y, z: t.z, px: t.px, py: t.py, pz: t.pz, damage: 8,
        // vanilla: it bounces off the target and falls
        hit: () => { t.dealt = true; t.vx *= -0.01; t.vy *= -0.1; t.vz *= -0.01; },
      };
    }
  },
});
{ const prevBlast = tnt.onExplode; tnt.onExplode = (x, y, z, p) => { prevBlast?.(x, y, z, p); endFight.onExplosion(x, y, z, p); }; }
// the wither boss: skull projectiles, the soul sand T summoning, its boss bar (game/witherRuntime.ts)
const witherRt = new WitherRuntime({
  chunks, entities, env, scene, stage: hudStage,
  explode: (x, y, z, power) => { entityHost.explode(x, y, z, power); entities.damageInRadius(x, y, z, power * 2, power, true); },
  dropItem: (s, x, y, z) => { items.spawn(s, x, y, z); },
  playerEffect: (p, id, amp, dur) => { if (p.id === '') stats.effects.add(id, amp, dur); },
  difficulty: () => currentDifficulty(),
  sfx: { play: (id, pos) => sfx.play(id, pos) },
});
const itemRenderer = new ItemEntityRenderer(env, atlas, atlasTexture);
scene.add(itemRenderer.group);
const playerModel = new PlayerModel(env);
scene.add(playerModel.group);
playerModel.setVisible(false);
const preview = new PlayerPreview();
const hand = new FirstPersonHand(itemRenderer.models);
hand.bowId = I.BOW;
player.camera.add(hand.object3d);
const shieldView = new ShieldView(env);
const sweepFx = new SweepFx(scene);
const tridents = new TridentManager();
const tridentView = new TridentRenderer(scene);
const spitView = new SpitRenderer(scene);
// boats (entities/vehicles/boat.ts physics, game/boatUse.ts player side, render/boat.ts model)
const boats = createBoats({
  player,
  read: (x, y, z) => chunks.world.getBlock(x, y, z),
  loaded: (x, z) => !!chunks.world.getChunk(x >> 4, z >> 4),
  ray: (targets) => raycast(chunks.world, player.camera.position, lookTmp, REACH, targets),
  creative: () => mode === 'creative',
  punchDamage: () => weaponAttack(isEmpty(inventory.held) ? null : inventory.held).damage,
  drop: (item, x, y, z) => { items.spawn(stackOf(item), x, y, z, 0, 0.1, 0, 10); },
  message: (m) => hud.toast(window.matchMedia?.('(pointer: coarse)').matches ? 'Tap SNEAK to dismount' : m),
});
const boatView = new BoatRenderer(scene, env);
scene.add(minecartView.group);
const leashView = new LeashRenderer(scene);
const enchantBooks = new EnchantBookRenderer(scene);
bookBlockHook = (x, y, z) => enchantBooks.blockChanged(x, y, z, chunks.world.getBlock(x, y, z) === ENCHANTING_TABLE_ID);
player.camera.add(shieldView.object3d);
const breakOverlay = new BreakOverlay();
scene.add(breakOverlay.object3d);
/** Crack on the door a zombie is breaking (entities/nav crackDoor hook). */
const doorCrack = new BreakOverlay();
scene.add(doorCrack.object3d);

// --- screens & menus ----------------------------------------------------------
let started = urlParams.get('menu') === '0' || autostart;
let discardSave = false;
let pointerLocked = false;
/** Seconds spent playing without the mouse and without any overlay up. */
let unlockedFor = 0;

/** Throw a stack in front of the player (Q, drops from the GUI, leftovers). */
function dropStack(s: Stack): void {
  if (isEmpty(s)) return;
  const p = player.position;
  items.dropFromPlayer({ id: s.id, count: s.count, dmg: s.dmg }, p.x, p.y + 1.4, p.z, player.yaw, player.pitch);
}

const screens = new ScreenHost(document.body, {
  addXp: (n) => stats.addXp(n),
  inventory,
  icons,
  gameMode: () => mode,
  guiScale: () => settings.guiScale,
  dropStack,
  onChange: () => { save.touch(); inventory.changed(); },
  renderPlayerPreview: (canvas, lookX, lookY) => {
    preview.render(canvas, lookX, lookY, {
      armor: inventory.armor.map((s) => (isEmpty(s) ? 0 : s.id)) as [number, number, number, number],
      heldItem: isEmpty(inventory.held) ? 0 : inventory.held.id,
      elytra: inventory.armor[1].id === I.ELYTRA,
    });
  },
});
screens.onClosed = () => lockOrPause();
// server mode: the server owns the inventory and every container
const windows = srv ? installWindows({
  srv, screens, inventory,
  getBlock: (x, y, z) => chunks.world.getBlock(x, y, z),
  level: () => stats.xpLevel,
  useSound: () => sfx.play('block.anvil.use'),
}) : null;

const teleportMenu = new TeleportMenu(document.body, () => settings.guiScale);
teleportMenu.onClose = () => lockOrPause();

const menus = new MenuHost(document.body, settings);
// --- sleeping (game/sleep.ts): the local player's bed state ------------------
const sleepUi = new SleepOverlay(document.body);
const sleepTimer = new SleepTimer();
let sleepPose: LyingPose | null = null;
let sleepNoticeTick = -999;
function leaveBed(): void {
  if (!sleepTimer.active) return;
  const p = sleepPose;
  sleepTimer.stop(); sleepUi.hide(); sleepPose = null;
  srvCombat?.wake();
  if (p) player.leaveBed(p.standX, p.standY, p.standZ);
  else player.sleeping = false;
  sleepHooks.localState = () => ({ sleeping: false, sleepTicks: 0 });
  tryLock();
}
sleepUi.onLeave = leaveBed;
function enterBed(hx: number, hy: number, hz: number, dx: number, dz: number): void {
  sleepPose = lyingPose(hx, hy, hz, dx, dz);
  const p = sleepPose;
  player.enterBed(p.x, p.y, p.z, p.yaw, dx, dz);
  sleepTimer.start(); sleepUi.show();
  sleepHooks.localState = () => ({ sleeping: sleepTimer.active, sleepTicks: sleepTimer.ticks });
  document.exitPointerLock();
  chat.add('', 'You are now sleeping', true);
}
/** Lie down in the bed this cell (head or foot) belongs to. */
function enterBedAt(x: number, y: number, z: number): void {
  const get = (bx: number, by: number, bz: number): number => chunks.world.getBlock(bx, by, bz);
  const id = get(x, y, z);
  let hx = x, hz = z, dir = id === BED_HEAD_ID ? bedDirection(get, x, y, z) : null;
  if (id === BED_FOOT_ID) {
    for (const [ddx, ddz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      if (get(x + ddx, y, z + ddz) === BED_HEAD_ID) { hx = x + ddx; hz = z + ddz; dir = [ddx, ddz]; break; }
    }
  }
  if (dir) enterBed(hx, y, hz, dir[0], dir[1]);
}
function tickSleep(): void {
  if (!sleepTimer.active) return;
  const night = sky.time > 0.52 || weather.isThundering();
  if (stats.dead || player.hurtTime > 0 || !night) { leaveBed(); return; }
  const reached = sleepTimer.tick();
  sleepUi.setFade(sleepTimer.fade);
  if (srv) return; // the server counts the sleepers and skips the night
  if (!reached && !(sleepTimer.ticks > 101 && tickCounter % 20 === 0)) return;
  const others = sleepHooks.remotePlayers();
  const d = sleepDecision([{ sleeping: true, sleepTicks: sleepTimer.ticks }, ...others], sleepHooks.percentage);
  if (d.skip) {
    skipToDawn({ setTime: (t) => { sky.time = t; }, wakeWeather: () => weather.wake(), resetRest: () => { stats.timeSinceRest = 0; } });
    sleepHooks.onSkip?.();
    leaveBed();
    hud.toast('You slept until dawn');
  } else if (d.total > 1 && tickCounter - sleepNoticeTick > 100) {
    sleepNoticeTick = tickCounter;
    hud.toast(sleepStatusText(d));
  }
}
menus.setGameMode(mode);
// chat (T, or / for a command): ONE overlay for single player and multiplayer.
// Created before installNet(), which asks chat.ts for the same instance.
const chat = getChatOverlay(document.body, () => Math.max(1, Math.min(3, Math.floor(innerWidth / 640) + 1)));
// multiplayer (src/net): Multiplayer screen, chat, remote players, host/replica hooks
/** Declared first only because the hooks below fire later than this call. */
let net: NetController;
// server mode: mobs, drops, orbs, arrows and boats are the server's; this draws them (client/entities.ts)
let srvBoss: { name: string; fraction: number } | null = null;
const srvBossBar = srv ? new BossBar(hudStage.content) : null;
const replica = srv ? new ServerReplica({
  srv, entities, player, light: lightAt,
  sound: (id, x, y, z) => { sfx.play(id, { x, y, z }); },
  onOffers: (_win, offers, xp, _level, title) => {
    srvTradeWin = _win;
    if (tradeScreen.open) tradeScreen.refresh(offers, xp);
    else { document.exitPointerLock(); tradeScreen.openTrade({ offers, xp, title: title || 'Trade' }, () => { srv.send({ t: 'close', win: _win }); lockOrPause(); }); }
  },
  onTradeClosed: () => { tradeScreen.close(); },
  onBoss: (name, f) => { srvBoss = f < 0 ? null : { name, fraction: f }; },
  onWeather: (rain, thunder) => {
    // the server runs the cycle: hold the local one still and let the levels ramp to what it says
    weather.clearTime = 0; weather.rainTime = weather.thunderTime = 1e9;
    weather.raining = rain > 0; weather.thundering = thunder > 0;
  },
  onLightning: (x, y, z) => { const c = player.camera.position; weatherRenderer.strike(x, y, z, c.x, c.y, c.z); },
}) : null;
// sign text and framed items (client/tiles.ts)
const srvTiles = srv && replica ? new TileDrawer({
  scene, others: (k) => replica.others(k), self: () => srv.welcome?.eid ?? 0,
  send: (m) => { srv.send(m); }, onFx: (h) => { srv.on('fx', h); },
}) : null;
const srvDraw = srv && replica ? new ProjectileDrawer({
  scene, env, camera: player.camera, others: (k) => replica.others(k), placeOf: (id) => replica.placeOf(id),
  self: () => srv.welcome?.eid ?? 0, rodTip: () => rodTip(), light: lightAt,
  thrown: thrownRenderer, potions: potionRenderer, bobber: bobberRenderer, tridents: tridentView,
}) : null;
// server mode: health, hunger, hits and item use are the server's (client/combat.ts)
const srvCombat = srv && replica ? installCombat({
  srv, stats, player, entities, others: (k) => replica.others(k),
  mode: () => mode, setMode: (m) => applyMode(m), onDeath: () => onPlayerDeath(),
  toast: (t) => hud.toast(t), chat: (t) => chat.add('', t, true),
  sound: (id, x, y, z) => { sfx.play(id, x === undefined || y === undefined || z === undefined ? undefined : { x, y, z }); },
  crit: (x, y, z, magic) => { if (magic) enchantHitBurst(particles, x, y - 0.9, z, 1.8, 8); else critBurst(particles, x, y - 0.9, z, 1.8); },
  eggBreak: (x, y, z) => thrownRenderer.shatter(x, y, z),
  splash: (x, y, z, c, l) => potionRenderer.shatter(x, y, z, c, l),
  fwBurst: (x, y, z, c) => fwBurst(x, y, z, c),
  enterBed: (x, y, z) => enterBedAt(x, y, z), leaveBed: () => leaveBed(),
  itemName: (id) => nameOf(id),
}) : null;
/** Server mode: right-click a block face with the held item (spawn egg, boat); the server answers with `ack`. */
function srvUseOn(hit: RayHit, dir: THREE.Vector3): void {
  if (!srv) return;
  const face = hit.nx > 0 ? 0 : hit.nx < 0 ? 1 : hit.ny > 0 ? 2 : hit.ny < 0 ? 3 : hit.nz > 0 ? 4 : 5;
  const f = rayFaceHit(player.camera.position, dir, hit);
  srv.send({ t: 'useOn', seq: srv.nextSeq(), x: hit.x, y: hit.y, z: hit.z, face, hx: f[0] - hit.x, hy: f[1] - hit.y, hz: f[2] - hit.z, hand: 0 });
}
if (srv) {
  net = installServerMode({
    srv, chunks, player, entityHost, sky, scene, env, menus, canvas: renderer.domElement, players: replica ?? undefined,
    spawnAt: (x, y, z, yaw, pitch) => { pending = { x, y, z, yaw, pitch }; player.setFrozen(true); player.position.set(x, y, z); },
  });
} else net = installNet({
  seed: WORLD_SEED, chunks, save, player, entities, entityHost, sky, scene, env, menus, canvas: renderer.domElement,
  health: () => stats.health, dead: () => stats.dead, guiScale: () => settings.guiScale,
  // a peer's command line: the host replays the ones that move world state
  // (see HOST_WORLD_COMMANDS) — everything else runs on the typist's client
  onPeerChat: (_name, text) => {
    if (!net?.session?.isHost) return;
    const cmd = parseCommand(text);
    if (cmd && HOST_WORLD_COMMANDS.has(cmd.name)) runChatCommand(commandContext, text);
  },
  // another player right-clicked one of the mobs we simulate
  useOnMob: (mob, held) => useOnMob(mobUseWorld, mob, held),
  // ...and the answer to one of our own clicks; the cost only lands if we still hold that item
  onMobUsed: (r, fx) => {
    const sent = pendingMobUse.get(r);
    pendingMobUse.delete(r);
    if (sent !== undefined && (isEmpty(inventory.held) ? 0 : inventory.held.id) === sent) applyMobUseFx(fx);
  },
});

// --- chat (T or /) + slash commands ------------------------------------------
/** Name for locally echoed lines; the net layer keeps the last one it used. */
function chatName(): string {
  try {
    const n = localStorage.getItem('opencraft.net.name');
    if (n) return n;
  } catch { /* storage unavailable */ }
  return 'Player';
}

/** Enter in the chat input: a session relays the line (net/** owns that), and
 *  single player echoes it locally and runs `/commands` against this world. */
function sendChat(text: string): void {
  // The pin is local-only: relaying it would put it in every peer's chat log,
  // and echoing it would leave it on screen. No relay, no echo, just the gate.
  if (isSecretCommand(text)) { runChatCommand(commandContext, text); return; }
  if (srv) {
    // the server echoes chat lines back to us; it has no commands yet, and the page's own would only change this copy
    if (text.startsWith('/')) chat.add('', 'Commands are not available on this server.', true); else srv.chat(text);
    return;
  }
  const session = net.session;
  if (session) {
    session.chat(text);
    // anyone may run commands: every command acts on the typist's own body,
    // inventory or game mode, so it runs where it was typed. A couple of them
    // move world state instead — see HOST_WORLD_COMMANDS.
    runChatCommand(commandContext, text);
    return;
  }
  chat.add(chatName(), text, false);
  runChatCommand(commandContext, text);
}

/**
 * Commands whose effect belongs to the host, not the typist: the sky clock and
 * the replicated mobs. A peer's line for one of these is replayed by the host,
 * so `/time set day` still moves the world for everyone.
 */
const HOST_WORLD_COMMANDS = new Set(['time', 'summon']);

// --- locator bar (vanilla 1.21.6) ---------------------------------------------
const DEG = 180 / Math.PI;
const locatorPeerViews: RemotePlayerView[] = [];
/** Tick the XP bar last changed on: vanilla shows XP for 100 ticks, then the locator bar. */
let xpShownUntilTick = 0;
let xpSig = '';

/**
 * Where the other players are, in camera-relative polar coordinates for the
 * locator bar (ui/gameHud owns the pixels). Null when there is nothing to draw:
 * single player, nobody else in the room, or the 100 ticks after the XP bar
 * changed — vanilla swaps back to XP for 5 seconds whenever XP moves.
 */
function locatorWaypoints(): LocatorWaypoint[] | null {
  const session = net.session;
  if (!session) return null;
  const p = player.position;
  const eyeY = p.y + 1.62;
  const cos = Math.cos(player.yaw), sin = Math.sin(player.yaw);
  const camPitch = player.pitch * DEG;
  const out: LocatorWaypoint[] = [];
  for (const v of session.remotePlayers(locatorPeerViews)) {
    // vanilla: crouching (or a mob head, or invisibility) hides you from the bar
    if ((v.state.flags & PF_SNEAK) !== 0) continue;
    const dx = v.v[0] - p.x, dy = v.v[1] + 1.62 - eyeY, dz = v.v[2] - p.z;
    const horiz = Math.hypot(dx, dz);
    if (horiz < 0.05) continue;
    // forward is (-sin yaw, -cos yaw), right is (cos yaw, -sin yaw)
    out.push({
      id: v.id,
      bearing: Math.atan2(dx * cos - dz * sin, -dx * sin - dz * cos) * DEG,
      dist: Math.hypot(dx, dy, dz),
      elev: Math.atan2(dy, horiz) * DEG - camPitch,
    });
  }
  return out.length ? out : null;
}

/** `/commands` -> the real game APIs. Never throws: ui/chatCommands answers in chat. */
const commandContext: CommandContext = {
  say: (text) => chat.add('', text, true),
  gameMode: () => mode,
  setGameMode: (m) => applyMode(m),
  time: () => sky.time,
  setTime: (t) => { sky.time = ((t % 1) + 1) % 1; },
  position: () => player.position,
  teleport: (x, y, z) => {
    player.teleportTo(x, y, z);
    if (mode === 'creative') player.flying = false;
  },
  // the same scan and the same landing spot the Y teleport picker uses
  structures: () => {
    const p = player.position;
    return hudTerrain.nearestStructures(p.x, p.z, 20).map((h) => ({ name: h.name, dist: h.dist, ...structureLanding(h) }));
  },
  // the other players of a session (remotePlayers is peers only, never you)
  players: () => {
    const session = net.session;
    if (!session) return [];
    const slots = new Map([...session.members.values()].map((m) => [m.id, m.slot]));
    return session.remotePlayers().map((v) => ({
      id: v.id, name: v.name, slot: slots.get(v.id) ?? -1, x: v.state.x, y: v.state.y, z: v.state.z,
    }));
  },
  give: (id, count) => {
    const left = inventory.add(id, count);
    if (left > 0) {
      // no room left: drop the remainder at the player, vanilla-style
      const p = player.position;
      items.spawn({ id, count: left, dmg: 0 }, p.x, p.y + 1, p.z, 0, 0.25, 0, 40);
    }
    inventory.changed();
  },
  spawnMob: (type, count) => {
    const p = player.position;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2; // ring them around the player
      entities.spawnMob(type, p.x + Math.cos(a) * 2.5, p.y + 1, p.z + Math.sin(a) * 2.5);
    }
  },
  kill: () => {
    if (stats.dead) return false;
    // void damage is the one kind that lands in creative too (vanilla /kill)
    stats.damage(1e9, { kind: 'void' });
    return true;
  },
  clearInventory: () => inventory.clear(),
  seed: () => WORLD_SEED,
};

// chat opens only while actually playing; the input swallows its own keys
chat.openGuard = () => started && pointerLocked && !menus.open && !screens.open && !teleportMenu.open && !stats.dead;
chat.onSend = (text) => sendChat(text);
// chat frees the mouse while typing (see ui/chat.ts); take it back on Enter/Esc
chat.onClose = () => { quietRelock = true; lockOrPause(); };

let lockWhyLogged = false;
/** Every lock decision, so a broken pointer is one console paste away. */
function lockLog(what: string, extra = ''): void {
  console.info(`[input] ${what}${extra ? ' ' + extra : ''}`,
    `| origin ${location.origin} secure ${window.isSecureContext} top ${window.top === window.self} locked ${!!document.pointerLockElement}`);
}
function lockWhy(e: unknown): void {
  if (lockWhyLogged) return;
  lockWhyLogged = true;
  console.warn('[input] the browser refused pointer lock:', e instanceof Error ? e.message : String(e));
}
function requestLock(): Promise<void> {
  lockLog('requestPointerLock');
  try {
    const p = renderer.domElement.requestPointerLock() as unknown;
    if (p instanceof Promise) return p.then(() => undefined, (e: unknown) => { lockWhy(e); });
  } catch (e) { lockWhy(e); }
  return Promise.resolve();
}

/**
 * Pointer lock - and Web Locks, which the per-tab identity needs - only exists
 * in a secure context. Browsers deny the request outright on a plain-http
 * origin that is not localhost (a lan ip or a hostname, say), and then nothing
 * can capture the mouse: no look, no clicks, E blocked, no way back. Say so at
 * boot instead of looking like a broken game.
 */
if (typeof window !== 'undefined' && !window.isSecureContext) {
  console.warn(`[input] ${location.origin} is not a secure context: the browser refuses pointer lock here (use https, or http://localhost / http://127.0.0.1)`);
}

/**
 * Browsers refuse a pointer-lock request for about a second after the user
 * pressed Esc (and Chrome restarts that window on every new attempt), which is
 * how "Esc broke the mouse" ends up permanent: every click is denied again.
 * So a resume retries across the window instead of firing once. The retries
 * stop as soon as the lock lands (pointerlockchange hides the menu for us).
 */
let lockTries = 0;
/** Closing chat must never end in the pause menu: if the browser refuses the
 *  re-lock, the next click on the canvas takes the mouse back instead. */
let quietRelock = false;
function tryLock(): void {
  if (pointerLocked || stats.dead) return;
  const attempt = lockTries++;
  requestLock().then(() => {
    if (pointerLocked) { lockTries = 0; return; }
    retryLock(attempt);
  }, () => retryLock(attempt));
}
function retryLock(attempt: number): void {
  if (attempt >= 16) { lockTries = 0; if (quietRelock) quietRelock = false; else pauseIfStuck(); return; } // ~4s: give up, leave the menu up
  setTimeout(() => { if (!pointerLocked) tryLock(); }, 250);
}

/** Re-capture the mouse after a screen closes; browsers refuse for a moment
 *  after an Esc-initiated unlock, in which case the game menu covers it. */
function lockOrPause(): void {
  if (pointerLocked || stats.dead || !started) return;
  tryLock();
}

/** Is a panel up that owns the mouse? None of them wants the pause menu behind it. */
function overlayOpen(): boolean {
  return menus.open || screens.open || teleportMenu.open || tradeScreen.open || horseScreen.open || llamaScreen.open || stats.dead || sleepUi.open || chat.open;
}

/**
 * Playing with the mouse uncaptured is a dead end: no look, no clicks, and
 * neither the browser's Esc-to-unlock nor the app's own Escape branch has
 * anything to do. Every path that can lose the lock funnels through here, so
 * the menu (which relocks on Resume) is always one click away.
 */
function pauseIfStuck(): void {
  if (!started || pointerLocked || overlayOpen()) return;
  menus.showPause();
}

function startGame(): void {
  started = true;
  menus.hide();
  tryLock();
}

menus.onPlay = () => startGame();
// a build with a game server: Multiplayer (title and pause menu) plays there, not in the legacy room flow
if (!srv && configuredServer()) menus.onJoinServer = menus.onMultiplayer = () => joinConfiguredServer(() => save.flush(true));
menus.onNewWorld = (m) => {
  discardSave = true;
  save.reset();
  save.setGameMode(m);
  save.flush(true);
  try { sessionStorage.setItem(AUTOSTART_KEY, '1'); } catch { /* ignore */ }
  location.reload();
};
menus.onResume = () => { menus.hide(); tryLock(); };
menus.onQuitToTitle = () => { save.flush(true); discardSave = true; location.reload(); };
menus.onRespawn = () => { if (srvCombat) srvCombat.respawn(); else respawn(); menus.hide(); tryLock(); };
menus.canToggleGameMode = () => !srv && commandsUnlocked(); // on a server the mode is the server's
menus.onGameModeToggle = () => {
  // same gate as /gamemode: the pin (see ui/chatCommands) keeps players from
  // flipping the mode out from under a survival world
  if (!commandsUnlocked()) { hud.toast('Type /pin <pin> to unlock game mode changes'); return; }
  applyMode(mode === 'survival' ? 'creative' : 'survival');
  hud.toast(mode === 'survival' ? 'Game mode: Survival' : 'Game mode: Creative');
};

if (!started) menus.showTitle();
else if (autostart) menus.showPause();

// --- window events ----------------------------------------------------------
window.addEventListener('resize', resize);
resize();
applyMode(mode);

// --- input ------------------------------------------------------------------
renderer.domElement.addEventListener('click', () => {
  // the click that lands on the canvas is a user gesture: the only reliable
  // way back into the game once the mouse was lost
  if (!pointerLocked && started && !menus.open && !screens.open && !teleportMenu.open && !tradeScreen.open && !horseScreen.open && !llamaScreen.open && !stats.dead && !sleepUi.open && !chat.open) tryLock();
});
document.addEventListener('pointerlockchange', () => {
  pointerLocked = document.pointerLockElement === renderer.domElement;
  lockLog(pointerLocked ? 'locked' : 'unlocked');
  if (pointerLocked) {
    quietRelock = false;
    if (menus.open && !stats.dead) menus.hide();
    return;
  }
  breakHeld = false;
  rightHeld = false;
  cancelUse();
  breakOverlay.clear();
  // Panels (inventory, chest, teleport) drop the lock themselves and must not
  // read as "open the menu"; anything else — Esc, alt-tab — pauses the game.
  if (started && !screens.open && !teleportMenu.open && !tradeScreen.open && !horseScreen.open && !llamaScreen.open && !menus.open && !stats.dead && !sleepUi.open && !chat.open) menus.showPause();
});
// A refused request (Safari and Firefox never resolve a promise for it, and
// Chrome rejects, then locks the API out for about a second after an Esc) must
// not leave the game playing without the mouse.
document.addEventListener('pointerlockerror', () => { lockLog('pointerlockerror'); /* tryLock's retry loop owns giving up */ if (lockTries === 0) pauseIfStuck(); });

// --- structure teleport (Y) --------------------------------------------------
/** Land the player at a structure: above the center block, with a safe head
 *  clearance, regardless of biome (villages sit on terrain, shipwrecks at sea). */
function openTeleportMenu(): void {
  const p = player.position;
  const hits = hudTerrain.nearestStructures(p.x, p.z, 20);
  teleportMenu.show(hits, (hit) => teleportTo(hit));
  document.exitPointerLock();
}

/** Where a structure hit lands you: the centre column, one block above the
 *  surface (sea level is the floor for biomes that map everything to water). */
function structureLanding(hit: StructureHit): { x: number; y: number; z: number } {
  const here = hudTerrain.columnInfo(hit.x, hit.z);
  return { x: hit.x + 0.5, y: Math.max(here.height + 1, SEA_LEVEL + 1), z: hit.z + 0.5 };
}

function teleportTo(hit: StructureHit): void {
  const { x, y, z } = structureLanding(hit);
  player.teleportTo(x, y, z);
  if (mode === 'creative') player.flying = false;
  hud.toast(`Teleported to ${hit.name}`);
  lockOrPause();
}

// --- death & respawn ----------------------------------------------------------
function onPlayerDeath(): void {
  player.dead = true;
  // peers' deaths reach the host through their state flags (see pushRemote);
  // the host's own death has nobody to notice it, so announce it here
  net.session?.announceDeath();
  breakHeld = false;
  rightHeld = false;
  cancelUse();
  breakOverlay.clear();
  screens.close();
  if (mode === 'survival' && !srv) { // server mode: the server drops them
    const p = player.position;
    for (const s of inventory.drainAll()) {
      if (enchLevel(s, ENCH.VANISHING_CURSE)) continue; // Curse of Vanishing: destroyed on death
      items.spawn(s, p.x, p.y + 0.5, p.z, (Math.random() - 0.5) * 0.3, 0.25, (Math.random() - 0.5) * 0.3, 40);
    }
    const drop = stats.xpDropOnDeath();
    if (drop > 0) xpOrbs.spawn(p.x, p.y + 0.5, p.z, drop);
  }
  document.exitPointerLock();
  menus.showDeath(stats.score);
}

function respawn(): void {
  stats.respawn();
  // beds (and the world spawn) are in the overworld
  travel.switchWithoutPortal('overworld');
  player.dead = false;
  const sp = save.spawn;
  const target = sp ?? (worldSpawn ? { x: worldSpawn.x + 0.5, y: 0, z: worldSpawn.z + 0.5 } : { x: 0.5, y: 0, z: 0.5 });
  pending = { x: target.x, z: target.z, y: sp ? sp.y : undefined };
  player.setFrozen(true);
  player.position.set(target.x, WORLD_HEIGHT - 2, target.z);
  // vanilla keeps the world's mobs across a player respawn
}

// --- using items (hold right mouse) -------------------------------------------
type UseKind = 'bow' | 'eat' | 'crossbow' | 'trident' | 'spear';
let rightHeld = false;
let use: { kind: UseKind; ticks: number; slot: number } | null = null;
/** Ticks since the last swing, for the attack cooldown meter. */
let attackTicks = 100;
let tickCounter = 0;

function cancelUse(): void { use = null; blocking = false; player.setUsingBow(false); }

// --- shield -------------------------------------------------------------------
let blocking = false;
let devBlock = false;
/** Ticks the shield has been held up (vanilla blocks only after 5). */
let shieldRaise = 0;
/** Ticks the shield stays down after an axe hit (vanilla 100). */
let shieldDisabled = 0;
/** An axe-wielding attacker knocks the shield out of use for 5 s (no axe mobs yet; hook for vindicators/players). */
function disableShield(): void { shieldDisabled = 100; blocking = false; }
let blockedTick = -1;

const isShield = (s: Stack): boolean => s.id === I.SHIELD && s.count > 0;
/** The shield in use: the main hand's, else the offhand's. */
function shieldStack(): Stack | null {
  if (isShield(inventory.held)) return inventory.held;
  if (isShield(inventory.offhand)) return inventory.offhand;
  return null;
}
/** Hits from the front while blocking are absorbed (vanilla: melee, arrows, blasts within ~90 degrees). */
function tryBlock(amount: number, src: DamageSource): boolean {
  if (!blocking || stats.dead) return false;
  if (src.kind !== 'melee' && src.kind !== 'player' && src.kind !== 'projectile' && src.kind !== 'explosion') return false;
  const shield = shieldStack();
  if (!shield) return false;
  if (src.x !== undefined && src.z !== undefined) {
    const dx = src.x - player.position.x, dz = src.z - player.position.z;
    const len = Math.hypot(dx, dz);
    // facing = (-sin yaw, -cos yaw)
    if (len > 1e-3 && (dx * -Math.sin(player.yaw) + dz * -Math.cos(player.yaw)) / len < 0) return false;
  }
  blockedTick = tickCounter;
  // an axe-wielding attacker knocks the shield down for 5 s (vanilla)
  if (src.kind === 'melee' && src.attackerId !== undefined) {
    const m = entities.mobs.find((x) => x.id === src.attackerId);
    if (m && (m.type === 'warden' || itemDef(m.heldItem).tool?.type === 'axe')) disableShield(); // a warden's blow also knocks the shield down for 5 s
  }
  sfx.shieldBlock();
  // vanilla: only hits of 3+ wear the shield, by 1 + floor(damage)
  if (mode === 'survival' && amount >= 3 && wearStack(inventory, shield, 1 + Math.floor(amount))) hud.toast('Your Shield broke');
  return true;
}

function attackCharge(): number {
  if (COMBAT_EDITION === 'bedrock') return 1; // Bedrock has no attack cooldown
  return chargeScale(attackTicks, weaponAttack(inventory.held).speed);
}

function startUse(): void {
  const held = inventory.held;
  if (isEmpty(held) || use) return;
  if (held.id === I.CROSSBOW) {
    // a loaded crossbow fires on click; otherwise hold to load (vanilla 25 ticks, -5 per Quick Charge)
    if (held.charged) { fireCrossbow(held); return; }
    if (mode === 'creative' || crossbowHasAmmo(inventory)) {
      use = { kind: 'crossbow', ticks: 0, slot: inventory.selected };
      player.setUsingBow(true);
      sfx.play(enchLevel(held, ENCH.QUICK_CHARGE) > 0 ? 'item.crossbow.quick_charge_1' : 'item.crossbow.loading_start');
    }
    return;
  }
  if (isSpear(held)) {
    if (srv) return; // no spear charge on the server yet (docs/server-model-todo.md)
    // hold use: lower the spear into a charge (game/spear.ts); damage is dealt each tick in tickSpearCharge
    use = { kind: 'spear', ticks: 0, slot: inventory.selected };
    return;
  }
  if (held.id === I.TRIDENT) {
    if (!tridentUsable(held)) return; // 1 durability left
    use = { kind: 'trident', ticks: 0, slot: inventory.selected };
    return;
  }
  if (held.id === I.BOW) {
    if (mode === 'creative' || inventory.countOf(I.ARROW) > 0 || enchLevel(held, ENCH.INFINITY) > 0) {
      use = { kind: 'bow', ticks: 0, slot: inventory.selected };
      player.setUsingBow(true);
    }
    return;
  }
  const food = itemDef(held.id).food;
  if (food && (stats.food < 20 || food.alwaysEdible || mode === 'creative')) {
    use = { kind: 'eat', ticks: 0, slot: inventory.selected };
  } else if (isDrinkable(held.id)) {
    // potions and milk: hold right mouse for the vanilla 1.6 s drink (the eat animation and timer)
    use = { kind: 'eat', ticks: 0, slot: inventory.selected };
  }
}

/** Use up the held item; its container (glass bottle, bucket, bowl) comes back in the slot or the inventory. */
function consumeHeldWithLeftover(): void {
  const left = emptiedBy(inventory.held.id);
  inventory.consumeHeld(1);
  if (left === 0) return;
  if (isEmpty(inventory.held)) { setStack(inventory.held, stackOf(left)); inventory.changed(); }
  else if (inventory.add(left) > 0) dropStack(stackOf(left));
}

/** Block-breaking time with Haste / Mining Fatigue applied. */
const breakSecondsFx = (...a: Parameters<typeof breakSeconds>): number => breakSeconds(...a) / Math.max(1e-6, mineMult(stats.effects));
/** Melee damage plus Strength (+3 / level) and Weakness (-4 / level), scaled by the swing charge like the base hit. */
function withEffects(dmg: number): number {
  const b = attackBonus(stats.effects);
  if (b === 0) return dmg;
  const c = COMBAT_EDITION === 'bedrock' ? 1 : attackCharge();
  return Math.max(0, dmg + b * (0.2 + c * c * 0.8));
}

/**
 * Spear charge (hold use): every mob the tip touches takes damage from the closing speed along the look
 * direction x the tier multiplier; knockback by stage (engaged full, tired half, disengaged none). The
 * charge ends after the tier's last stage. Rider dismounting is a hook (no mounts exist yet).
 */
function tickSpearCharge(ticks: number): void {
  const held = inventory.held;
  const stage = chargeStage(held.id, ticks);
  if (stage === 'ended') { cancelUse(); return; }
  const eye = player.camera.position;
  const dir = lookTmp;
  player.camera.getWorldDirection(dir);
  const ride = mountState.mob;
  const pv = ride ? { x: ride.vx, y: ride.vy, z: ride.vz } : player.velocity;
  if (ticks < (spearTier(held.id)?.delay ?? 0)) return; // kinetic_weapon delay: the spear is still being lowered
  for (const m of entities.mobs) {
    if (!m.alive || m === ride) continue;
    if (rayBoxDist(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, m, CHARGE_REACH, 0.2) < 0) continue;
    const rel = relativeSpeedBps([pv.x, pv.y, pv.z], [m.vx, m.vy, m.vz], [dir.x, dir.y, dir.z]);
    let dmg = chargeDamage(held.id, rel);
    if (dmg < CHARGE_MIN_DAMAGE) continue;
    dmg += enchantDamageBonus(held, m.type, COMBAT_EDITION);
    const looting = enchLevel(held, ENCH.LOOTING);
    const fire = enchLevel(held, ENCH.FIRE_ASPECT) * 80;
    const kb = rel >= CHARGE_MIN_KB_BPS ? 0.4 * chargeKnockbackScale(stage) : 0;
    if (entities.attack(m, dmg, player.position.x, player.position.z, kb, { looting, fireTicks: fire, yaw: player.yaw })) {
      if (mode === 'survival' && wearStack(inventory, held, 1)) { hud.toast(`Your ${nameOf(held.id)} broke`); cancelUse(); return; }
    }
  }
}

/**
 * Spear jab on a left click: reach window 2..4 blocks, pierces every mob on the line, real cooldown,
 * Lunge on top. Returns true when the click was consumed (a jab, or a cooldown with nothing to mine);
 * false lets the click start breaking the block in front.
 */
function spearJab(eye: THREE.Vector3, dir: THREE.Vector3, blockHit: ReturnType<typeof raycast>): boolean {
  const held = inventory.held;
  let blockDist = Infinity;
  if (blockHit) {
    const f = rayFaceHit(eye, dir, blockHit);
    blockDist = Math.hypot(f[0] - eye.x, f[1] - eye.y, f[2] - eye.z);
  }
  const ready = jabReady(held.id, attackTicks);
  const bodies = [...entities.mobs, ...entities.remotePlayers.filter((p) => !p.creative && !p.spectator)];
  const targets = ready ? jabTargets(bodies, eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, blockDist) : [];
  if (targets.length === 0 && blockHit && blockDist <= REACH) {
    // facing a block in reach: the click still mines it, but a ready jab lunges regardless (vanilla)
    if (ready) spearLunge(held);
    return false;
  }
  if (!ready) return true;
  for (const t of targets) {
    if ((entities.mobs as readonly unknown[]).includes(t.mob)) attackMob(t.mob as Mob);
    else attackPlayer(t.mob as PlayerTarget);
  }
  spearLunge(held);
  attackTicks = 0;
  return true;
}

function spearLunge(held: typeof inventory.held): void {
  const lvl = lungeLevel(held);
  if (lvl > 0 && !lungeBlocked({ food: stats.food, creative: mode === 'creative', inWater: player.inWater, gliding: player.glider.gliding || mountState.mob !== null })) {
    const [lx, lz] = lungeVelocity(lvl, player.yaw);
    player.velocity.x += lx; player.velocity.z += lz;
    player.fovPulse = Math.min(6, 2 * lvl);
    if (mode === 'survival') {
      stats.addExhaustion(lungeExhaustion(lvl));
      if (!isEmpty(inventory.held) && wearStack(inventory, inventory.held, lungeExtraDurability(lvl))) hud.toast(`Your ${nameOf(held.id)} broke`);
    }
  }
}

function tickUse(): void {
  if (!use) return;
  if (use.slot !== inventory.selected || isEmpty(inventory.held) || !rightHeld) {
    // releasing a drawn bow looses the arrow
    // server mode: client/combat.ts sends `release`, the server looses the arrow
    if (!srv && use.kind === 'bow' && rightHeld === false && use.slot === inventory.selected) shootBow(use.ticks);
    if (!srv && use.kind === 'trident' && rightHeld === false && use.slot === inventory.selected && use.ticks >= 10) throwTrident();
    cancelUse();
    return;
  }
  use.ticks++;
  if (use.kind === 'spear') { tickSpearCharge(use.ticks); return; }
  if (use.kind === 'crossbow') {
    const need = Math.max(0, 25 - 5 * enchLevel(inventory.held, ENCH.QUICK_CHARGE));
    if (use.ticks === Math.floor(need / 2)) sfx.play('item.crossbow.loading_middle');
    if (use.ticks >= need) {
      const cb = inventory.held;
      if (srv || loadCrossbow(inventory, cb, mode !== 'creative')) { sfx.play('item.crossbow.loading_end'); }
      cancelUse();
      return;
    }
  }
  if (use.kind === 'eat' && use.ticks > 6 && use.ticks % 4 === 0) {
    if (isDrinkable(inventory.held.id)) sfx.drink(); else {
      sfx.eat();
      eatCrumbs(particles, icons.get(inventory.held.id), player.position.x, player.position.y + 1.62, player.position.z, player.yaw);
    }
  }
  if (use.kind === 'eat' && use.ticks >= eatTicksOf(inventory.held.id)) {
    if (srv) { cancelUse(); return; } // the server finishes the meal
    const held = inventory.held;
    const food = itemDef(held.id).food;
    if (isDrinkable(held.id)) {
      drinkStack(stats.effects, stats.effectTarget, held);
      if (mode === 'survival') consumeHeldWithLeftover();
    } else if (food) {
      stats.eat(food.hunger, food.saturation);
      if (held.id === I.CHORUS_FRUIT) chorusTeleport(outerHost, player.position.x, player.position.y, player.position.z);
      applyFoodEffects(stats.effects, held.id, held.dmg);
      if (mode === 'survival') consumeHeldWithLeftover();
    }
    cancelUse();
  }
}

/** Everything the shared projectile launchers need from the live game. */
function lookDirection(): THREE.Vector3 {
  // from yaw / pitch, not the camera: the camera lags a frame and flips in third person
  const cp = Math.cos(player.pitch);
  return new THREE.Vector3(-Math.sin(player.yaw) * cp, Math.sin(player.pitch), -Math.cos(player.yaw) * cp);
}

function projEnv(): ProjEnv {
  const dir = lookDirection();
  const p = player.position;
  return {
    inv: inventory, survival: mode === 'survival', eye: { x: p.x, y: p.y + 1.52, z: p.z }, dir,
    shootArrow: (...a) => entities.shootArrow(...a), tridents,
    launchRocket: (x, y, z, v, s) => { const r = rockets.launch(x, y, z, v, s, false); r.shooter = fwPlayer; },
    broke: (n) => hud.toast(`Your ${n} broke`),
  };
}

function shootBow(ticks: number): void {
  const a = shootBowAt(projEnv(), ticks);
  if (!a) return;
  sfx.bowShoot(bowPower(ticks));
  player.startSwing();
}

/** Loose a loaded crossbow (see fireCrossbowAt). */
function fireCrossbow(cb: Stack): void {
  fireCrossbowAt(projEnv(), cb);
  sfx.play('item.crossbow.shoot');
  player.startSwing();
}

/** Release a charged trident: thrown at 2.5 b/t along the view (vanilla ThrownTrident). Riptide launches the player instead (in water or rain). */
function throwTrident(): void {
  const st = inventory.held;
  if (!tridentUsable(st)) return;
  const rip = enchLevel(st, ENCH.RIPTIDE);
  if (rip > 0) {
    if (!player.inWater && !isRaining(player.position)) return; // Riptide works in water or rain only
    const dir = lookDirection();
    // vanilla: speed 3 * (1 + level) / 4
    const k = (3 * (1 + rip)) / 4;
    player.velocity.x += dir.x * k; player.velocity.y += dir.y * k + (player.onGround ? 0.4 : 0); player.velocity.z += dir.z * k;
    if (mode === 'survival') wearStack(inventory, st, 1);
    riptideTicks = 20; riptideHit.clear();
    sfx.play('item.trident.riptide_1');
    return;
  }
  if (!throwTridentAt(projEnv(), inventory.selected)) return;
  sfx.play('item.trident.throw');
  player.startSwing();
}

/** Ticks left of a Riptide spin attack (touching mobs hurts them for 8, once each). */
let riptideTicks = 0;
const riptideHit = new Set<number>();
function tickRiptide(): void {
  if (riptideTicks <= 0) return;
  riptideTicks--;
  const p = player.position;
  for (const m of entities.mobs) {
    if (!m.alive || riptideHit.has(m.id)) continue;
    const hw = m.width / 2 + 0.6;
    if (Math.abs(m.x - p.x) < hw && Math.abs(m.z - p.z) < hw && m.y < p.y + 2 && m.y + m.height > p.y - 0.2) {
      riptideHit.add(m.id);
      entities.attack(m, 8 + (m.inWater || isRaining(m) ? 2.5 * enchLevel(inventory.held, ENCH.IMPALING) : 0), p.x, p.z, 0.8);
      sfx.play('item.trident.hit', { x: m.x, y: m.y, z: m.z });
    }
  }
}

// --- breaking (hold left mouse) ----------------------------------------------
/** Seconds between two breaks while the button stays down (vanilla: 5 ticks). */
const BREAK_COOLDOWN = 0.25;
let breakHeld = false;
let breakProgress = 0;
let breakCooldown = 0;
let breakTarget: { x: number; y: number; z: number } | null = null;

/** Sound and particles of a block action (game/blockActions.ts); in server mode other players' too (client/blocks.ts). */
function playBlockFx(kind: blockRules.BlockFx, x: number, y: number, z: number, d: number): void {
  switch (kind) {
    case 'break': particles.burst(x, y, z, d); sfx.blockBreak(x, y, z, d); break;
    case 'place': sfx.blockPlace(x, y, z, d); break;
    case 'door': { const s = redstoneSound(kind, chunks.world.getBlock(x, y, z), d); if (s) sfx.redstone(s, x, y, z); else sfx.door(x, y, z, d === 1); break; }
    case 'note': case 'click': case 'piston': { const s = redstoneSound(kind, chunks.world.getBlock(x, y, z), d); if (s) sfx.redstone(s, x, y, z); break; }
    case 'ignite': sfx.ignite(x, y, z); particles.burst(x, y, z, FIRE_ID); break;
    case 'bucket': sfx.bucket((d & 1) === 1, d >= 2, x, y, z); break;
    case 'fizz':
      for (let i = 0; i < 8; i++) particles.fuse(x + Math.random() - 0.5, y + Math.random() * 0.5, z + Math.random() - 0.5);
      sfx.fireExtinguish(x + 0.5, y + 0.5, z + 0.5);
      break;
    case 'bonemeal': // vanilla: happy_villager sparkles over the block
      for (let i = 0; i < 6; i++) hearts.spawn(x + Math.random(), y + Math.random() * 0.8, z + Math.random(), 'happy');
      break;
  }
}

/** The offline game's side of the block rules in game/blockActions.ts: the world they edit... */
const blockEnv: blockRules.BlockEnv = {
  get world() { return chunks.world; },
  get dim() { return chunks.dimension; },
  canEdit: (x, y, z) => chunks.canEdit(x, y, z),
  setBlock: (x, y, z, id) => { chunks.setBlock(x, y, z, id); },
  rnd: Math.random,
  dropBlock: (s, x, y, z) => { items.spawnBlockDrop(s, x, y, z); },
  dropXp: (n, x, y, z) => { xpOrbs.spawn(x, y, z, n); },
  fx: playBlockFx,
  primeTnt: (x, y, z) => { tnt.prime(x, y, z); },
  igniteFire: (x, y, z) => { fires.ignite(x, y, z); },
  // don't place inside the player
  occupied: (x, y, z) => {
    const p = player.position;
    return x + 1 > p.x - 0.35 && x < p.x + 0.35 && z + 1 > p.z - 0.35 && z < p.z + 0.35 && y + 1 > p.y && y < p.y + 1.85;
  },
  toast: (text) => hud.toast(text),
  broken: (x, y, z, id, drop) => {
    // breaking gold (ore, blocks, gilded blackstone) or a chest enrages the piglins that see it
    if (PIGLIN_ANGERING_BLOCKS.has(id) && drop) angerPiglinsNear(x, y, z, undefined, true);
    // a chest spills its contents and its slot record goes with it — otherwise
    // re-placing a chest would resurrect its old loot
    if (chestFacing(id) >= 0) spillChest(x, y, z, drop);
    if (id === BARREL_ID) spillBarrel(x, y, z, drop);
    if (isFurnaceBlock(id)) spillFurnace(x, y, z, drop);
    if (id === BREWING_STAND_ID) spillBrewing(x, y, z, drop);
  },
  batch: (fn) => chunks.batchEdits(fn),
};
/** ...and the local player. */
const blockActor: blockRules.BlockActor = {
  get x() { return player.position.x; }, get y() { return player.position.y; }, get z() { return player.position.z; },
  get yaw() { return player.yaw; },
  get creative() { return mode !== 'survival'; },
  get held() { return inventory.held; },
  get helmet() { return inventory.armor[0]; },
  get effects() { return stats.effects; },
  consumeHeld: (n) => { inventory.consumeHeld(n); },
  wearHeld: (n) => {
    const held = inventory.held, name = nameOf(held.id);
    if (wearStack(inventory, held, n)) hud.toast(`Your ${name} broke`);
  },
  swapHeld: (id) => blockRules.swapHeld(inventory, id, (s) => blockActor.give(s)),
  give: (s) => {
    if (isEmpty(inventory.held)) { setStack(inventory.held, s); inventory.changed(); }
    else if (inventory.add(s.id, s.count, s.dmg) > 0) dropStack(s);
  },
  exhaust: (n) => stats.addExhaustion(n),
};
/** Server mode: digging and right-clicks on blocks are intents, predicted with the same rules (client/blocks.ts). */
const srvBlocks = srv ? installBlocks({
  srv, chunks, actor: blockActor, fx: playBlockFx, occupied: blockEnv.occupied, toast: (text) => hud.toast(text),
  serverBlock: isFence, serverItem: (id) => isSpawnEgg(id) || boats.isBoatItem(id) || id === I.END_CRYSTAL,
  consumeHeld: (n) => inventory.consumeHeld(n),
}) : null;
srv?.on('ack', (m) => { if (!m.ok) windows?.resync(); }); // a refused action gives its predicted item back

/** Break the targeted block: drops, XP, tool wear; underwater plants leave their water behind. */
function breakBlock(hit: { x: number; y: number; z: number }): void {
  blockRules.breakBlock(blockEnv, blockActor, hit.x, hit.y, hit.z);
}

/** Empty a chest into the world and forget its saved contents. An unopened
 *  chest has no record yet — its loot is rolled on demand from the position
 *  hash, so roll it now to drop what vanilla would. */
function spillChest(x: number, y: number, z: number, drop: boolean): void {
  const key = `${x},${y},${z}`;
  let contents = save.chest(key);
  if (contents) save.forgetChest(key);
  else contents = chestLoot(x, y, z);
  if (!drop) return;
  for (const s of contents) if (!isEmpty(s)) items.spawnBlockDrop(s, x, y, z);
}

/** A barrel only ever holds what players put in it (no rolled loot). */
function spillBarrel(x: number, y: number, z: number, drop: boolean): void {
  const key = `${x},${y},${z}`;
  const contents = save.chest(key);
  if (!contents) return;
  save.forgetChest(key);
  if (!drop) return;
  for (const s of contents) if (!isEmpty(s)) items.spawnBlockDrop(s, x, y, z);
}

function spillBrewing(x: number, y: number, z: number, drop: boolean): void {
  const b = brewing.remove(`${x},${y},${z}`);
  if (!b || !drop) return;
  for (const s of [...b.bottles, b.ingredient, b.fuel]) if (!isEmpty(s)) items.spawnBlockDrop(s, x, y, z);
}

function spillFurnace(x: number, y: number, z: number, drop: boolean): void {
  const f = furnaces.remove(`${x},${y},${z}`);
  if (!f || !drop) return;
  for (const s of [f.input, f.fuel, f.output]) if (!isEmpty(s)) items.spawnBlockDrop(s, x, y, z);
}

/** Deterministic loot for the chest at a position. */
function chestLoot(x: number, y: number, z: number): Stack[] {
  // the only chests the nether generates are nether-fortress ones
  const kind = chunks.dimension === 'nether' ? netherLootKind(WORLD_SEED, x, y, z)
    : chunks.dimension === 'end' ? endLootKind(WORLD_SEED, x, y, z)
    : hudTerrain.structureLootAt(x, y, z) ?? 'default';
  return rollChestLoot(mulberry32(Math.floor(hash2(x, y ^ 0xa3, z) * 4294967296)), kind);
}

/** What an empty bucket's ray stops at: any block, or a water source to scoop. */
const BUCKET_TARGETS = IS_TARGETABLE.slice();
BUCKET_TARGETS[WATER_ID] = 1;
BUCKET_TARGETS[LAVA_ID] = 1;

/** Right click with a bucket: pour a water source / scoop one up (swaps full/empty in survival). */
function useBucket(item: number, dir: THREE.Vector3): void {
  const hit = raycast(chunks.world, player.camera.position, dir, REACH, item === I.BUCKET ? BUCKET_TARGETS : IS_TARGETABLE);
  if (hit) blockRules.useBucket(blockEnv, blockActor, item, hit);
}

/** Right click with flint and steel (or a fire charge): light TNT, a portal or a
 *  fire on the targeted block. Returns true if the click was consumed. */
function useFlintAndSteel(dir: THREE.Vector3, charge = false): boolean {
  const hit = raycast(chunks.world, player.camera.position, dir, REACH);
  return hit !== null && blockRules.ignite(blockEnv, blockActor, hit, charge);
}

/** Where the ray hits the face `hit` reports (world space). */
function rayFaceHit(origin: THREE.Vector3, dir: THREE.Vector3, hit: RayHit): [number, number, number] {
  const o = [origin.x, origin.y, origin.z], d = [dir.x, dir.y, dir.z];
  const n = [hit.nx, hit.ny, hit.nz], c = [hit.x, hit.y, hit.z];
  const a = n[0] !== 0 ? 0 : n[1] !== 0 ? 1 : 2;
  const t = (c[a] + (n[a] > 0 ? 1 : 0) - o[a]) / d[a];
  return [o[0] + d[0] * t, o[1] + d[1] * t, o[2] + d[2] * t];
}

/**
 * Right-click interaction: doors swing open/closed (both halves), chest lids
 * toggle, crafting tables and furnaces open their screens, beds sleep until
 * dawn. Returns true when the click was consumed; ctrl/shift+click skips this
 * and places a block instead.
 */
/** Player enchantment seed (vanilla xpSeed): fixes the table's offers until the next enchant. */
let enchantSeed = typeof (save.stats as { enchantSeed?: unknown } | null)?.enchantSeed === 'number'
  ? (save.stats as { enchantSeed: number }).enchantSeed | 0 : (Math.random() * 0x7fffffff) | 0;

function interact(x: number, y: number, z: number, id: number): boolean {
  // an eye of ender in an empty end portal frame; the twelfth opens the portal
  if (blockRules.insertEyeOfEnder(blockEnv, blockActor, x, y, z)) return true;
  // a vault opens to a trial key
  if (id === VAULT_ID || id === VAULT_OPENED_ID) {
    const r = trials.useVault(x, y, z, isEmpty(inventory.held) ? 0 : inventory.held.id);
    if (r === 'opened') { if (mode === 'survival') inventory.consumeHeld(1); hud.toast('The vault opens'); }
    else hud.toast(r === 'spent' ? 'The vault is empty' : 'The vault needs a trial key');
    return true;
  }
  // Chest: a closed chest opens the UI and lifts the lid (vanilla does both).
  // An already-open chest just shuts again — right-clicking the lid toggles it
  // without reopening the screen.
  if (chestFacing(id) >= 0) {
    if (isChestOpen(id)) closeChestAt(x, y, z);
    else openChest(x, y, z);
    return true;
  }
  if (id === BARREL_ID) {
    openBarrel(x, y, z);
    return true;
  }
  if (id === CRAFTING_TABLE_ID) {
    screens.openCraftingTable();
    return true;
  }
  if (id === ENCHANTING_TABLE_ID) {
    screens.openEnchanting({
      bookshelves: countBookshelves((bx, by, bz) => chunks.world.getBlock(bx, by, bz), x, y, z),
      level: () => stats.xpLevel,
      spendLevels: (n) => stats.spendLevels(n),
      seed: () => enchantSeed,
      rerollSeed: () => { enchantSeed = (Math.random() * 0x7fffffff) | 0; save.setStats(statsBlob()); },
    });
    return true;
  }
  const anvil = anvilState(id);
  if (anvil) {
    screens.openAnvil({
      level: () => stats.xpLevel, spendLevels: (n) => stats.spendLevels(n),
      // vanilla: 12% per use (survival) to degrade intact -> chipped -> damaged -> gone
      onUse: () => {
        sfx.play('block.anvil.use');
        if (mode !== 'survival' || Math.random() >= 0.12) return;
        const cur = anvilState(chunks.world.getBlock(x, y, z));
        if (!cur) return;
        chunks.setBlock(x, y, z, cur[0] >= 2 ? AIR : ANVIL_FAMILIES[cur[0] + 1][cur[1]]);
        if (cur[0] >= 2) screens.close();
      },
    });
    return true;
  }
  if (id === GRINDSTONE_ID) {
    screens.openGrindstone({ addXp: (n) => stats.addXp(n), onUse: () => sfx.play('block.anvil.use') });
    return true;
  }
  if (id === SMITHING_TABLE_ID) {
    screens.openSmithing();
    return true;
  }
  if (isFurnaceBlock(id)) {
    screens.openFurnace(furnaces.get(`${x},${y},${z}`), () => save.touch());
    return true;
  }
  if (id === BREWING_STAND_ID) {
    screens.openBrewing(brewing.get(`${x},${y},${z}`), () => save.touch());
    return true;
  }
  // doors (both halves), fence gates and trapdoors swing
  if (blockRules.toggleBlock(blockEnv, x, y, z)) return true;
  if (id === BED_FOOT_ID || id === BED_HEAD_ID) {
    const verdict = bedVerdict({
      dimension: chunks.dimension, time: sky.time, thundering: weather.isThundering(),
      hostiles: entities.mobs.filter((m) => m.alive && isHostileType(m.type)),
      px: player.camera.position.x, py: player.camera.position.y, pz: player.camera.position.z,
    });
    if (verdict === 'explode') { entityHost.explode(x + 0.5, y + 0.5, z + 0.5, 5); return true; }
    if (verdict === 'monsters') { hud.toast(BED_MESSAGES.monsters); return true; }
    if (verdict === 'day') { save.setSpawn({ x: x + 0.5, y, z: z + 0.5 }); hud.toast(BED_MESSAGES.day); return true; }
    save.setSpawn({ x: x + 0.5, y, z: z + 0.5 });
    if (sky.time > 0.52 || weather.isThundering()) {
      enterBedAt(x, y, z);
    } else {
      hud.toast('Respawn point set. You can only sleep at night');
    }
    return true;
  }
  return false;
}

/** Open a chest: lift the lid (preserving facing) and show its screen. */
function openChest(x: number, y: number, z: number): void {
  if (mode === 'survival') angerPiglinsNear(x, y, z, undefined, true);
  const key = `${x},${y},${z}`;
  let contents = save.chest(key);
  if (!contents) {
    contents = chestLoot(x, y, z);
    save.recordChest(key, contents);
  }
  // lift the lid visually (vanilla TOGGLE would also do this — chest is
  // special-cased because right-click opens the UI too, so we want both).
  const cur = chunks.world.getBlock(x, y, z);
  const openId = chestOpenOf(cur);
  if (openId !== undefined) chunks.setBlock(x, y, z, openId);
  sfx.chest(x, y, z, true);
  screens.openChest(contents, 'Chest', () => { closeChestAt(x, y, z); save.touch(); });
}

/** Open a barrel: 27 slots stored like a chest's, but it starts empty and has no lid to lift. */
function openBarrel(x: number, y: number, z: number): void {
  const key = `${x},${y},${z}`;
  let contents = save.chest(key);
  if (!contents) {
    contents = Array.from({ length: 27 }, () => emptyStack());
    save.recordChest(key, contents);
  }
  sfx.chest(x, y, z, true);
  screens.openChest(contents, 'Barrel', () => { sfx.chest(x, y, z, false); save.touch(); });
}

/** Closing the chest UI should drop the lid (vanilla: right-click an open
 *  chest to close it, or break the chest, or move far enough away). */
function closeChestAt(x: number, y: number, z: number): void {
  const cur = chunks.world.getBlock(x, y, z);
  const closed = chestClosedOf(cur);
  if (closed !== undefined) { chunks.setBlock(x, y, z, closed); sfx.chest(x, y, z, false); }
}

/** Place the held block against `hit`. Returns true when a block went in. */
function placeBlock(sel: number, hit: RayHit, dir: THREE.Vector3): boolean {
  return blockRules.placeBlock(blockEnv, blockActor, sel, hit, rayFaceHit(player.camera.position, dir, hit));
}

/** Left click on a mob: damage scaled by the weapon and its cooldown, vanilla crits and knockback. */
function attackMob(mob: Mob): void {
  const held = inventory.held;
  const tool = isEmpty(held) ? undefined : itemDef(held.id).tool;
  const hit = meleeHit({
    held: isEmpty(held) ? null : held, charge: attackCharge(), targetType: mob.type, targetWet: mob.inWater || isRaining(mob),
    falling: player.velocity.y < 0 && !player.flying, onGround: player.onGround, inWater: player.inWater,
    sprinting: player.sprinting, fallDistance: player.fallen,
  });
  hit.damage = withEffects(hit.damage);
  sfx.attack(hit, attackCharge() > 0.9, player.sprinting);
  if (srvCombat) { srvCombat.attack(mob.id); attackTicks = 0; return; } // the server decides the hit
  if (hit.crit) critBurst(particles, mob.x, mob.y, mob.z, mob.height);
  if (!isEmpty(held) && held.ench && held.ench.length > 0 && hit.damage > 0) enchantHitBurst(particles, mob.x, mob.y, mob.z, mob.height, 8);
  const px = player.position.x, pz = player.position.z;
  const looting = enchLevel(held, ENCH.LOOTING);
  entities.attack(mob, hit.damage, px, pz, 0.4, { looting, fireTicks: hit.fireTicks, kbLevels: hit.kbLevels, yaw: player.yaw });
  if (hit.knockback > 0.4 && player.sprinting) {
    // vanilla: a knockback hit slows the attacker and ends the sprint
    player.velocity.x *= 0.6; player.velocity.z *= 0.6;
    player.sprinting = false;
  }
  if (hit.sweep) {
    // sweep: every other mob within 1 block (box grown 1 x 0.25 x 1 around the target), 80% knockback
    for (const m of entities.mobs) {
      if (m === mob || !m.alive) continue;
      if (Math.abs(m.x - mob.x) > 1 + (m.width + mob.width) / 2 || Math.abs(m.z - mob.z) > 1 + (m.width + mob.width) / 2) continue;
      if (m.y > mob.y + mob.height + 0.25 || m.y + m.height < mob.y - 0.25) continue;
      if ((m.x - px) ** 2 + (m.z - pz) ** 2 > 9) continue;
      entities.attack(m, hit.sweepDamage, px, pz, 0.4 * 0.8, { looting });
    }
    sweepFx.spawn(player.camera);
  }
  if (hit.smash) {
    // mace smash: no fall damage, Wind Burst launches the player back up
    player.resetFall();
    const wb = enchLevel(held, ENCH.WIND_BURST);
    if (wb > 0) player.velocity.y = windBurstVelocity(wb);
    else player.velocity.y = Math.max(player.velocity.y, 0.01);
  }
  attackTicks = 0;
  if (mode === 'survival') {
    stats.addExhaustion(EXHAUST_ATTACK);
    if (tool && !isEmpty(held)) {
      const name = nameOf(held.id);
      if (wearStack(inventory, held, tool.type === 'sword' || tool.type === 'mace' || tool.type === 'trident' || tool.type === 'spear' ? 1 : 2)) hud.toast(`Your ${name} broke`);
    }
  }
}

/**
 * Left click on another player: the same weapon math as a mob (charge, crit,
 * sprint knockback, Fire Aspect) less the sweep, which vanilla only ever
 * spreads to the mobs around the target. In a session a client's swing is
 * forwarded to the host, which applies it and tells the body it hurt.
 */
function attackPlayer(target: PlayerTarget): void {
  const held = inventory.held;
  const tool = isEmpty(held) ? undefined : itemDef(held.id).tool;
  const hit = meleeHit({
    held: isEmpty(held) ? null : held, charge: attackCharge(), targetType: 'player',
    falling: player.velocity.y < 0 && !player.flying, onGround: player.onGround, inWater: player.inWater,
    sprinting: player.sprinting, fallDistance: player.fallen,
  });
  hit.damage = withEffects(hit.damage);
  sfx.attack(hit, attackCharge() > 0.9, player.sprinting);
  if (srvCombat) { if (target.eid !== undefined) srvCombat.attack(target.eid); attackTicks = 0; return; }
  entities.attackPlayer(target, hit.damage, player.position.x, player.position.z, hit.knockback,
    { fireTicks: hit.fireTicks });
  if (hit.knockback > 0.4 && player.sprinting) {
    // vanilla: a knockback hit slows the attacker and ends the sprint
    player.velocity.x *= 0.6; player.velocity.z *= 0.6;
    player.sprinting = false;
  }
  if (hit.smash) {
    // a mace smash lands on players too: no fall damage, Wind Burst throws us up
    player.resetFall();
    const wb = enchLevel(held, ENCH.WIND_BURST);
    if (wb > 0) player.velocity.y = windBurstVelocity(wb);
    else player.velocity.y = Math.max(player.velocity.y, 0.01);
  }
  attackTicks = 0;
  if (mode === 'survival') {
    stats.addExhaustion(EXHAUST_ATTACK);
    if (tool && !isEmpty(held)) {
      const name = nameOf(held.id);
      if (wearStack(inventory, held, tool.type === 'sword' || tool.type === 'mace' || tool.type === 'trident' || tool.type === 'spear' ? 1 : 2)) hud.toast(`Your ${name} broke`);
    }
  }
}

const lookTmp = new THREE.Vector3();

document.addEventListener('mousedown', (e) => {
  // nothing owns the mouse and we do not have it: this click is the gesture
  // that takes it back (the pause menu's Resume cannot, a key cannot)
  if (!pointerLocked) { if (!overlayOpen()) tryLock(); return; }
  // spectators can't break or place blocks; GUIs own the mouse
  if (player.spectator || stats.dead || player.sleeping) return;
  const dir = lookTmp;
  player.camera.getWorldDirection(dir);
  if (e.button === 0) {
    player.startSwing();
    const eye = player.camera.position;
    const hit = raycast(chunks.world, eye, dir, REACH);
    if (netherRt.deflect(eye, dir, REACH)) return; // punched a ghast fireball back
    if (endFight.melee(eye, dir, REACH, hit ? Math.hypot(...rayFaceHit(eye, dir, hit).map((v, i) => v - [eye.x, eye.y, eye.z][i]) as [number, number, number]) : Infinity)) return;
    if (isSpear(inventory.held) && spearJab(eye, dir, hit)) return;
    const spearHeld = isSpear(inventory.held);
    const mobHit = spearHeld ? null : entities.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, REACH);
    const playerHit = spearHeld ? null : entities.raycastPlayer(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, REACH);
    {
      // a boat under the crosshair (nearer than any block / mob) takes the punch
      const bf = hit ? rayFaceHit(eye, dir, hit) : null;
      const near = Math.min(bf ? Math.hypot(bf[0] - eye.x, bf[1] - eye.y, bf[2] - eye.z) : Infinity, mobHit?.dist ?? Infinity, playerHit?.dist ?? Infinity);
      if (srv && replica) {
        // server mode: a boat, an end crystal or the dragon under the crosshair is struck there
        const b = replica.boatOnRay(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, Math.min(REACH, near)) ?? replica.bossOnRay(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, Math.min(REACH + 2, near));
        if (b) { srv.send({ t: 'strike', id: b.id }); return; }
      }
      if (!spearHeld && boats.punch(eye, dir, near)) return;
      if (!spearHeld && minecarts.punch(eye, dir, near)) return;
    }
    if (mobHit || playerHit) {
      let blockDist = Infinity;
      if (hit) {
        const f = rayFaceHit(eye, dir, hit);
        blockDist = Math.hypot(f[0] - eye.x, f[1] - eye.y, f[2] - eye.z);
      }
      // whichever body the crosshair met first; a block in front of both wins
      if (playerHit && (!mobHit || playerHit.dist <= mobHit.dist)) {
        if (playerHit.dist < blockDist) { attackPlayer(playerHit.player); return; }
      } else if (mobHit && mobHit.dist < blockDist) {
        attackMob(mobHit.mob);
        return;
      }
    }
    breakHeld = true;
    breakProgress = 0;
    breakCooldown = 0;
    breakTarget = null;
    return;
  }
  if (e.button === 1) {
    // creative middle click: pick the targeted block
    if (mode !== 'creative') return;
    const hit = raycast(chunks.world, player.camera.position, dir, REACH);
    if (!hit) return;
    const id = blockItemOf(hit.id);
    if (id > 0) {
      const slot = inventory.slots.findIndex((s) => s.id === id);
      if (slot >= 0 && slot < HOTBAR) inventory.setSelected(slot);
      else setStack(inventory.held, stackOf(id));
      inventory.changed();
    }
    return;
  }
  if (e.button !== 2) return;
  rightHeld = true;
  const held = inventory.held;
  // riding: the carrot / fungus stick boosts the mount
  if (mountState.mob && rideUseItem(held, (n) => mode === 'survival' && wearStack(inventory, held, n) && (hud.toast(`Your ${nameOf(held.id)} broke`), true))) { player.startSwing(); return; }
  const hit = raycast(chunks.world, player.camera.position, dir, REACH);
  {
    // climb into a boat (not while sneaking; a nearer block or mob wins)
    const bf = hit ? rayFaceHit(player.camera.position, dir, hit) : null;
    const pe = player.camera.position;
    const md = entities.raycast(pe.x, pe.y, pe.z, dir.x, dir.y, dir.z, REACH)?.dist ?? Infinity;
    if (srv && replica) {
      // server mode: the server decides what a click on a boat or a mob does (actions/mobs.ts)
      const bd = bf ? Math.hypot(bf[0] - pe.x, bf[1] - pe.y, bf[2] - pe.z) : Infinity;
      const b = player.sitting ? null : replica.boatOnRay(pe.x, pe.y, pe.z, dir.x, dir.y, dir.z, Math.min(REACH, bd, md));
      const m = b ? null : mobInFront(hit, dir);
      if (b || m) { srv.send({ t: 'interact', id: b ? b.id : m!.id, hand: 0 }); player.startSwing(); return; }
      if (hit && !isEmpty(held) && (isSpawnEgg(held.id) || boats.isBoatItem(held.id))) { srvUseOn(hit, dir); player.startSwing(); return; }
    }
    if (!srv && boats.use(pe, dir, Math.min(bf ? Math.hypot(bf[0] - pe.x, bf[1] - pe.y, bf[2] - pe.z) : Infinity, md), player.sneaking)) { player.startSwing(); return; }
    // a cart under the crosshair: climb in; a minecart held against a rail: put it on the track
    if (!srv && !player.sneaking && minecarts.use(held, hit, pe, dir, Math.min(bf ? Math.hypot(bf[0] - pe.x, bf[1] - pe.y, bf[2] - pe.z) : Infinity, md))) { player.startSwing(); return; }
  }
  // a mob in front of the block: feed / shear it or trade with a villager
  // (spawn eggs handle their own mob case; village.interact declines eggs)
  const mob = mobInFront(hit, dir);
  if (mob) {
    // leads first: a lead in hand leashes (even a horse), a held mob is released
    if (leadUseOnMob(mob, isEmpty(held) ? 0 : held.id, I.LEAD, leadEnv)) { player.startSwing(); return; }
    // wandering trader: trade screen; llama: feed / tame / chest (entities/trader)
    if (traders.interact(mob, held, { ctrl: e.ctrlKey, sneaking: player.sneaking })) { player.startSwing(); return; }
    // wolves: bone = tame attempt, tamed wolf = sit / stand or heal with meat
    if (isRightClickable(mob) && (isEmpty(held) || !isSpawnEgg(held.id)) && useMob(mob, held, petUseFx)) { player.startSwing(); return; }
    // saddle a pig / strider, feed / saddle / armour a horse, or climb on (game/mounts.ts)
    if ((isEmpty(held) || !isSpawnEgg(held.id)) && mountUse(mob, held, mountEnv)) { player.startSwing(); return; }
    if (!isEmpty(held) && !isSpawnEgg(held.id) && itemUseLikely(mob, held) && useMob(mob, held, itemUseFx)) { player.startSwing(); return; }
    if (!e.ctrlKey && !player.sneaking && village.interact(mob, held)) { player.startSwing(); return; }
  }
  // server mode: a click on a block is the server's to resolve, predicted here (client/blocks.ts)
  if (srvBlocks?.useOn(hit, hit ? rayFaceHit(player.camera.position, dir, hit) : null, player.sneaking,
    () => raycast(chunks.world, player.camera.position, dir, REACH, BUCKET_TARGETS))) { player.startSwing(); return; }
  if (srv) {
    // server mode: nothing else is decided on this page. A container opens its window, an item the
    // server has a use for (food, bow, armour, pearl, rod...) goes out as `use`; only the animation is local.
    if (hit && !e.ctrlKey && !player.sneaking && windows) {
      const ih = blockRules.stepOverDecoration(chunks.world, hit);
      if (containerKind(ih.id) && windows.useBlock(ih.x, ih.y, ih.z)) { player.startSwing(); return; }
    }
    if (!isEmpty(held) && isItem(held.id) && srvCombat) {
      const u = srvCombat.use(held, player.glider.gliding);
      if (u === 'done') player.startSwing();
      else if (u === 'hold') startUse();
    }
    return;
  }
  // interactables first (doors, chests, tables, beds); ctrl/shift+click force-places
  // a torch / plant on top of a chest or furnace must not swallow the click
  // a fence: tie the mobs the player leads to a knot there, or untie a knot (entities/leash.ts)
  if (hit && !e.ctrlKey && !player.sneaking && isFence(hit.id) && leadUseOnFence(hit.x, hit.y, hit.z, leadEnv) !== 'none') { player.startSwing(); return; }
  if (hit && !e.ctrlKey && !player.sneaking) {
    const ih = blockRules.stepOverDecoration(chunks.world, hit);
    if (interact(ih.x, ih.y, ih.z, ih.id)) { player.startSwing(); return; }
  }
  if (isEmpty(held)) return;
  if (isSpawnEgg(held.id)) {
    const ok = useSpawnEgg(held.id, hit, dir);
    if (ok) player.startSwing();
    return;
  }
  if (isItem(held.id)) {
    if (equipHeld(inventory)) { inventory.changed(); player.startSwing(); return; }
    if (held.id === I.FISHING_ROD) { useRod(); return; }
    if (held.id === I.FIRE_CHARGE) {
      if (useFlintAndSteel(dir, true)) player.startSwing();
      return;
    }
    if (held.id === FLINT_AND_STEEL_ID) {
      if (useFlintAndSteel(dir)) player.startSwing();
      return;
    }
    if (held.id === I.END_CRYSTAL) {
      if (hit && endFight.placeCrystal(hit.x, hit.y, hit.z)) {
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      }
      return;
    }
    if (held.id === I.FIREWORK_ROCKET) {
      const dur = rocketDuration(held);
      if (player.glider.fireRocket(dur, Math.random, rocketStars(held).length)) {
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      } else if (hit) {
        rockets.launch(hit.x + hit.nx * 0.5 + 0.5, hit.y + hit.ny * 0.5 + 0.5, hit.z + hit.nz * 0.5 + 0.5, [0, 0, 0], held, true);
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      }
      return;
    }
    if (held.id === I.EYE_OF_ENDER) {
      if (endRt.throwEye(player.camera.position)) {
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      }
      return;
    }
    if (held.id === I.ENDER_PEARL) {
      // vanilla: thrown at 1.5 blocks/tick, 20-tick cooldown
      if (netherRt.throwPearl(player.camera.position, dir)) {
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      }
      return;
    }
    if (boats.isBoatItem(held.id)) {
      if (boats.place(held.id, player.camera.position, dir)) {
        player.startSwing();
        if (mode === 'survival') inventory.consumeHeld(1);
      }
      return;
    }
    if (held.id === I.BUCKET || held.id === I.WATER_BUCKET || held.id === I.LAVA_BUCKET) {
      useBucket(held.id, dir);
      player.startSwing();
      return;
    }
    if (held.id === I.GLASS_BOTTLE) {
      // fill from a water source (survival swaps one bottle for a Water Bottle)
      const wh = raycast(chunks.world, player.camera.position, dir, REACH, BUCKET_TARGETS);
      if (wh && blockRules.fillBottle(blockEnv, blockActor, wh)) {
        player.startSwing();
      } else if (endFight.takeBreath(player.camera.position, dir, REACH)) {
        // the dragon's lingering breath fills the bottle (vanilla: the cloud shrinks by 0.5 per bottle)
        player.startSwing();
        if (mode === 'survival') {
          inventory.consumeHeld(1);
          if (isEmpty(inventory.held)) { setStack(inventory.held, stackOf(I.DRAGON_BREATH)); inventory.changed(); }
          else if (inventory.add(I.DRAGON_BREATH) > 0) dropStack(stackOf(I.DRAGON_BREATH));
        }
      }
      return;
    }
    if (held.id === I.SPLASH_POTION || held.id === I.LINGERING_POTION) {
      const eye = player.camera.position;
      potions.throwPotion(eye.x, eye.y - 0.1, eye.z, player.yaw, player.pitch, held.id, held.dmg);
      player.startSwing();
      if (mode === 'survival') inventory.consumeHeld(1);
      return;
    }
    if (useFarmItem(held, hit)) { player.startSwing(); return; }
    startUse();
    return;
  }
  if (!hit) return;
  if (placeBlock(held.id, hit, dir)) {
    const px = hit.x + hit.nx, py = hit.y + hit.ny, pz = hit.z + hit.nz;
    // a carved pumpkin / jack o'lantern on an iron T builds an iron golem
    village.onPlace(px, py, pz, chunks.world.getBlock(px, py, pz));
    // three wither skeleton skulls on a soul sand T summon the wither
    witherRt.onPlace(px, py, pz, chunks.world.getBlock(px, py, pz));
    player.startSwing();
    if (mode === 'survival') inventory.consumeHeld(1);
  }
});
/** Lead interactions (entities/leash.ts): a lead is a plain item entity. */
const leadEnv: LeadUseEnv = {
  get creative() { return mode === 'creative'; },
  consumeHeld: (n) => inventory.consumeHeld(n),
  dropLead: (x, y, z) => { items.spawn(stackOf(I.LEAD), x, y, z, (Math.random() - 0.5) * 0.2, 0.2, (Math.random() - 0.5) * 0.2); },
};
const leashCtx = {
  player: { get x() { return player.position.x; }, get y() { return player.position.y; }, get z() { return player.position.z; }, get alive() { return !stats.dead; } },
  mobById: (id: number) => entities.mobs.find((m) => m.id === id && m.alive),
  dropLead: leadEnv.dropLead,
  knotValid: (x: number, y: number, z: number) => isFence(chunks.world.getBlock(x, y, z)),
};
/** The living mob the crosshair is on, if it is nearer than the targeted block. */
function mobInFront(hit: RayHit | null, dir: THREE.Vector3): Mob | null {
  const eye = player.camera.position;
  const mobHit = entities.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, REACH);
  if (!mobHit) return null;
  if (hit) {
    const f = rayFaceHit(eye, dir, hit);
    if (Math.hypot(f[0] - eye.x, f[1] - eye.y, f[2] - eye.z) <= mobHit.dist) return null;
  }
  return mobHit.mob;
}

/** The world side of a mob use (entities/mobUse.ts): wool lands in the item manager, a lit creeper hisses. */
const mobUseWorld: MobUseWorld = {
  spawnItem: (stack, x, y, z, vx, vy, vz) => { items.spawn(stack, x, y, z, vx, vy, vz); },
  ignite: (x, y, z) => sfx.ignite(x, y, z),
  rnd: Math.random,
};
const petUseFx = (mob: Mob, held: Stack): MobUseFx | null => usePet(mobUseWorld, mob, held);
const itemUseFx = (mob: Mob, held: Stack): MobUseFx | null => useItemOnMob(mobUseWorld, mob, held);

/** What a mob interaction cost the local player (creative pays nothing). */
function applyMobUseFx(fx: MobUseFx): void {
  if (mode !== 'survival') return;
  const held = inventory.held;
  if (fx.wear && !isEmpty(held)) {
    const name = nameOf(held.id);
    if (wearStack(inventory, held, fx.wear)) hud.toast(`Your ${name} broke`);
  }
  if (fx.consume) inventory.consumeHeld(fx.consume);
  if (fx.give) {
    const g: Stack = { id: fx.give[0], count: fx.give[1], dmg: fx.give[2] };
    if (isEmpty(inventory.held)) { setStack(inventory.held, g); inventory.changed(); }
    else if (inventory.add(g.id, g.count, g.dmg) > 0) dropStack(g);
  }
}

/** Requests sent to the peer simulating a mob: request id -> the item held when it was sent. */
const pendingMobUse = new Map<number, number>();

/**
 * Run a right-click on `mob`. Our own mobs are handled here; a mob another
 * peer simulates is handled there and answered through `onMobUsed`. Returns
 * true when the click was consumed.
 */
function useMob(mob: Mob, held: Stack, run: (mob: Mob, held: Stack) => MobUseFx | null): boolean {
  const req = net.useOnMob(mob, held);
  if (req !== null) {
    if (pendingMobUse.size > 32) pendingMobUse.clear();
    pendingMobUse.set(req, isEmpty(held) ? 0 : held.id);
    return true;
  }
  const fx = run(mob, held);
  if (fx) applyMobUseFx(fx);
  return fx !== null;
}

/** Farmland at (x, y - 0.1, z) trampled to dirt; the crop on it pops off. */
function trampleAt(x: number, y: number, z: number): void {
  const bx = Math.floor(x), by = Math.floor(y - 0.1), bz = Math.floor(z);
  if (!chunks.canEdit(bx, by, bz)) return;
  const above = chunks.world.getBlock(bx, by + 1, bz);
  if (isCrop(above)) {
    for (const s of cropDrops(above, Math.random)) items.spawnBlockDrop(s, bx, by + 1, bz);
    chunks.setBlock(bx, by + 1, bz, AIR);
  }
  chunks.setBlock(bx, by, bz, DIRT_ID);
}

/** Hoe tilling, planting seeds / carrots / potatoes on farmland, throwing eggs. */
function useFarmItem(held: Stack, hit: RayHit | null): boolean {
  if (held.id === I.EGG) {
    const eye = player.camera.position;
    thrown.throwEgg(eye.x, eye.y - 0.1, eye.z, player.yaw, player.pitch);
    if (mode === 'survival') inventory.consumeHeld(1);
    return true;
  }
  // bone meal, hoes and shovels, seeds (game/blockActions.ts)
  return hit !== null && blockRules.useFarmItem(blockEnv, blockActor, hit);
}

/** Spawn egg on a block face: the mob appears in the cell beside it (in the
 *  clicked cell itself when that has no collision, e.g. grass). On a mob of the
 *  same type vanilla makes a baby; there are no babies yet, so that does nothing. */
function useSpawnEgg(eggId: number, hit: RayHit | null, dir: THREE.Vector3): boolean {
  const type = spawnEggMob(eggId);
  if (!type) return false;
  const mob = mobInFront(hit, dir);
  if (mob) {
    // same type: a baby (only animals have babies here); anything else: nothing
    if (mob.type !== type || !spawnBaby({ spawn: (t, x, y, z) => entities.spawnMob(t, x, y, z) }, type, mob.x, mob.y, mob.z)) return false;
    if (mode === 'survival') inventory.consumeHeld(1);
    return true;
  }
  if (!hit) return false;
  const inPlace = collisionOf(hit.id).length === 0;
  const [x, y, z] = inPlace ? eggSpawnPos(hit.x, hit.y, hit.z, 0, 0, 0) : eggSpawnPos(hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz);
  const m = entities.spawnMob(type, x, y, z);
  if (!m) return false;
  // vanilla plays no sound for an egg
  if (mode === 'survival') inventory.consumeHeld(1);
  return true;
}

document.addEventListener('mouseup', (e) => {
  if (e.button === 0) {
    breakHeld = false;
    breakProgress = 0;
    breakTarget = null;
    breakOverlay.clear();
  }
  if (e.button === 2) {
    // let the next tick see the release so a drawn bow shoots
    rightHeld = false;
  }
});
document.addEventListener('blur', () => {
  breakHeld = false;
  breakProgress = 0;
  breakTarget = null;
  rightHeld = false;
  breakOverlay.clear();
});
document.addEventListener('contextmenu', (e) => e.preventDefault());
document.addEventListener('wheel', (e) => {
  if (!pointerLocked) return;
  inventory.setSelected(inventory.selected + (e.deltaY > 0 ? 1 : -1));
});

// nearest structures for the debug HUD — recomputed only when the player
// crosses into a new chunk (the scan runs every structure generator once).
const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const compassOf = (dx: number, dz: number): string =>
  COMPASS[((Math.round(Math.atan2(dx, -dz) / (Math.PI / 4)) % 8) + 8) % 8];
let structChunkX = NaN, structChunkZ = NaN;
let nearest: StructureHit[] = [];
document.addEventListener('keydown', (e) => {
  if (player.sleeping && (e.code === 'Escape' || e.code === 'ShiftLeft')) { leaveBed(); return; }
  // browsers eat the F-keys, so the debug HUD rides on Shift+3 (vanilla's F3
  // spot) and the camera on V
  if (e.code === 'Digit3' && e.shiftKey) {
    e.preventDefault();
    const mode3 = perf.cycle();
    if ((mode3 === PerfMode.Full) !== debugOn) {
      debugOn = !debugOn;
      hud.toggleDebug();
      savePrefs({ ...prefs, debug: debugOn });
    }
    return;
  }
  // a container screen owns the keyboard (Esc/E close, 1-9 swap, Q drop, search typing)
  if (screens.open) {
    if (screens.handleKey(e)) e.preventDefault();
    return;
  }
  // the villager trade overlay (its own DOM, see entities/village/tradeScreen)
  if (horseScreen.open || llamaScreen.open) return; // closes itself on Esc / E
  if (tradeScreen.open) {
    e.preventDefault();
    if (e.code === 'Escape' || e.code === 'KeyE') tradeScreen.close();
    return;
  }
  if (e.code === 'Escape') {
    if (teleportMenu.open) { e.preventDefault(); teleportMenu.hide(); lockOrPause(); return; }
    if (menus.open) { if (menus.handleEscape()) e.preventDefault(); return; }
    // Nothing open: Esc is "pause", and it must not depend on the browser
    // dropping the lock for us (it does not, once the lock is already gone).
    if (started && !stats.dead) {
      e.preventDefault();
      menus.showPause();
      document.exitPointerLock();
    }
    return;
  }
  if (menus.open || stats.dead) return;
  if (e.code === 'KeyG' && !e.repeat) cycleQuality();
  if (e.code === 'KeyE' && !e.repeat) { screens.openInventory(); return; }
  if (e.code === 'KeyY' && !e.repeat) { openTeleportMenu(); return; } // T is chat now
  if (e.code === 'KeyQ' && !e.repeat) {
    const s = inventory.held;
    if (windows) windows.drop(e.ctrlKey); // the server throws it and corrects the slot
    else if (!isEmpty(s)) {
      const n = e.ctrlKey ? s.count : 1;
      dropStack({ id: s.id, count: n, dmg: s.dmg });
      s.count -= n;
      if (s.count <= 0) clearStack(s);
      inventory.changed();
    }
    return;
  }
  if (e.code === 'KeyX' && !e.repeat) {
    // swap main hand and offhand (vanilla's F; F is flight here)
    const a = inventory.held, b = inventory.offhand;
    const tmp = { id: a.id, count: a.count, dmg: a.dmg };
    setStack(a, b);
    setStack(b, tmp);
    inventory.changed();
    windows?.swapOffhand();
    return;
  }
  if (/^Digit[1-9]$/.test(e.code)) inventory.setSelected(parseInt(e.code.slice(5), 10) - 1);
});

// --- spawn ------------------------------------------------------------------
// While frozen (no physics), hover above the *saved* location so chunks load
// around where the player will resume, not around the default spawn.
const savedPlayer = save.player;
// a fresh world starts on dry temperate land near the origin, like vanilla
// (also where a respawn without a bed goes: every player shares this point)
const worldSpawn = hudTerrain.findSpawn();
let spawnTries = 0;
interface PendingSpawn { x: number; z: number; y?: number; yaw?: number; pitch?: number; flying?: boolean }
let pending: PendingSpawn | null = srv?.welcome ? { x: srv.welcome.x, y: srv.welcome.y, z: srv.welcome.z, yaw: srv.welcome.yaw, pitch: srv.welcome.pitch } : savedPlayer
  ? { x: savedPlayer.x, z: savedPlayer.z, y: savedPlayer.y, yaw: savedPlayer.yaw, pitch: savedPlayer.pitch, flying: savedPlayer.flying }
  : { x: worldSpawn.x + 0.5, z: worldSpawn.z + 0.5 };
player.position.set(pending!.x, WORLD_HEIGHT - 2, pending!.z);
player.setFrozen(true);

function trySpawn(): void {
  if (!pending) return;
  const chunk = chunks.world.getChunk(Math.floor(pending.x) >> 4, Math.floor(pending.z) >> 4);
  if (!chunk || chunk.state !== ChunkState.Ready) return;
  let y = pending.y;
  if (y === undefined) {
    // no saved height: a random safe column near the point (game/spawn.ts), so
    // players neither stack on one block nor land in a tree or a pond
    const ready = (x: number, z: number): boolean => chunks.world.getChunk(x >> 4, z >> 4)?.state === ChunkState.Ready;
    const spot = pickSpawn(chunks.world, pending.x, pending.z, SPAWN_RADIUS, ready);
    if (spot) {
      pending.x = spot.x; pending.z = spot.z;
      y = spot.y + 0.02;
    } else {
      if (++spawnTries < 120) return; // neighbours still loading, or an unlucky round: next frame
      const sy = chunks.spawnY(Math.floor(pending.x), Math.floor(pending.z));
      if (sy < 0) return;
      y = sy + 1.02;
    }
    spawnTries = 0;
  }
  player.teleportTo(pending.x, y, pending.z);
  if (pending.yaw !== undefined) player.yaw = pending.yaw;
  if (pending.pitch !== undefined) player.pitch = pending.pitch;
  player.flying = mode === 'creative' ? (pending.flying ?? false) : false;
  pending = null;
  if (!entitiesRestored && !srv) {
    // saved mobs/items/arrows/orbs/TNT come back once the player stands in a loaded world
    entitiesRestored = true;
    if (!travel.travelling) { deserializeEntities(entityStores(), save.entities[chunks.dimension]); boats.load((save.entities[chunks.dimension] as { boats?: unknown } | undefined)?.boats); falling.load((save.entities[chunks.dimension] as { falling?: unknown } | undefined)?.falling); }
  }
}

// --- entity persistence (entities/persist.ts) -------------------------------
let entitiesRestored = false;
function entityStores(): EntityStores { return { entities, items, xp: xpOrbs, tnt: tnt.charges }; }
save.entitySource = () => entitiesRestored ? { ...serializeEntities(entityStores()), boats: boats.serialize(), falling: falling.serialize() } : save.entities[chunks.dimension];

// --- main loop --------------------------------------------------------------
const clock = new THREE.Clock();
let physicsAcc = 0;
const lookDir = new THREE.Vector3();

/**
 * Cardinal facing from yaw, vanilla F3 wording. The camera's horizontal
 * forward is (-sin yaw, -cos yaw); vanilla axes: north = -Z, east = +X.
 */
function facing(yaw: number): string {
  const dx = -Math.sin(yaw);
  const dz = -Math.cos(yaw);
  if (Math.abs(dz) >= Math.abs(dx)) {
    return dz < 0 ? 'north (Towards negative Z)' : 'south (Towards positive Z)';
  }
  return dx < 0 ? 'west (Towards negative X)' : 'east (Towards positive X)';
}

/** Time from first frame until every chunk in view was meshed (0 = still loading). */
const loadStart = performance.now();
let worldReadyMs = 0;

const itemHost = {
  getBlock: (x: number, y: number, z: number) => chunks.world.getBlock(x, y, z),
  loaded: (x: number, z: number) => chunks.canEdit(Math.floor(x), 0, Math.floor(z)),
  inventory: {
    add: (id: number, count: number, dmg = 0, ench?: Stack['ench']) => {
      const left = inventory.add(id, count, dmg, ench);
      return left;
    },
  },
  player: playerBox,
  light: lightAt,
  onBurn: (x: number, y: number, z: number) => particles.burst(Math.floor(x), Math.floor(y), Math.floor(z), FIRE_ID),
};
/** World difficulty (no settings UI yet): localStorage 'opencraft.difficulty', default normal. */
function currentDifficulty(): 'peaceful' | 'easy' | 'normal' | 'hard' {
  try {
    const d = localStorage.getItem('opencraft.difficulty');
    if (d === 'peaceful' || d === 'easy' || d === 'hard') return d;
  } catch { /* storage unavailable */ }
  return 'normal';
}
/** Rain falling on a point (open sky, wet biome): Impaling / Riptide. */
const isRaining = (p: { x: number; y: number; z: number }): boolean => weather.isRainingAt(p.x, p.y, p.z);
const tridentHost: TridentHost = {
  world: chunks.world,
  get mobs() { return entities.mobs; },
  player: playerBox,
  hit: (m, d, fx, fz) => entities.attack(m, d, fx, fz, 0.4),
  isWet: (m) => m.inWater || isRaining(m),
  give: (st, slot) => {
    // back to the slot it was thrown from when that is still empty
    const cell = slot !== undefined && slot >= 0 ? inventory.slots[slot] : null;
    if (cell && isEmpty(cell)) { setStack(cell, { id: st.id, count: 1, dmg: st.dmg, ench: st.ench }); inventory.changed(); return true; }
    return inventory.add(st.id, 1, st.dmg, st.ench) === 0;
  },
  channel: (m) => {
    if (weather.isThundering() && weather.exposed(m.x, m.y + m.height, m.z)) weatherFx.strikeAt(Math.floor(m.x), Math.floor(m.z));
  },
  light: lightAt,
  sound: (id, x, y, z) => sfx.play(id, { x, y, z }),
};
const xpHost = {
  getBlock: (x: number, y: number, z: number) => chunks.world.getBlock(x, y, z),
  loaded: (x: number, z: number) => chunks.canEdit(Math.floor(x), 0, Math.floor(z)),
  player: playerBox,
  // Mending eats the orb first (2 durability per point), the rest is XP
  stats: { addXp: (n: number) => { const left = mendingAbsorb(inventory, n); if (left > 0) stats.addXp(left); } },
  light: lightAt,
};

/** Fire / cactus contact for PlayerStats. */
function contactFlags(): { inFire: boolean; touchingCactus: boolean } {
  const p = player.position;
  const bx = Math.floor(p.x), bz = Math.floor(p.z), by = Math.floor(p.y);
  const f0 = chunks.world.getBlock(bx, by, bz), f1 = chunks.world.getBlock(bx, by + 1, bz);
  const inFire = f0 === FIRE_ID || f1 === FIRE_ID || f0 === SOUL_FIRE_ID || f1 === SOUL_FIRE_ID;
  // soul fire burns twice as hard as fire (vanilla 2 vs 1 per hit)
  if ((f0 === SOUL_FIRE_ID || f1 === SOUL_FIRE_ID) && mode === 'survival') stats.damage(2, { kind: 'fire' });
  let cactus = false;
  for (let dy = 0; dy <= 1 && !cactus; dy++) {
    for (const [dx, dz] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (chunks.world.getBlock(bx + dx, by + dy, bz + dz) !== CACTUS_ID) continue;
      // only when the player's box really reaches into the cactus' (slightly shrunk) cell
      const near = Math.abs(p.x - (bx + dx + 0.5)) < 0.3 + 0.5 - 0.0625 && Math.abs(p.z - (bz + dz + 0.5)) < 0.3 + 0.5 - 0.0625;
      if (near) { cactus = true; break; }
    }
  }
  return { inFire, touchingCactus: cactus };
}

let furnaceSaveTimer = 0;
const furnaceHost = { getBlock: (x: number, y: number, z: number) => chunks.world.getBlock(x, y, z), setBlock: (x: number, y: number, z: number, id: number) => chunks.setBlock(x, y, z, id) };

function gameTick(): void {
  tickCounter++;
  tickSleep();
  const fellBefore = player.onGround ? 0 : player.fallen;
  player.tick();
  // FarmBlock.fallOn: landing hard on farmland tramples it (never while sneaking)
  if (!srv && fellBefore > 0.5 && player.onGround && !player.sneaking && !player.flying
    && trampleCheck(chunks.world, player.position.x, player.position.y, player.position.z, fellBefore, 0.6, 1.8, Math.random)) {
    trampleAt(player.position.x, player.position.y, player.position.z);
  }
  if (!srv) stats.addMovement(player.movementExhaustion()); // the server counts exhaustion from the moves it sees
  const contact = contactFlags();
  if (!srv) stats.tick({
    mode, inWater: player.inWater, headInWater: player.headInWater, inLava: player.inLava,
    onGround: player.onGround, sprinting: player.sprinting, pos: player.position,
    inFire: contact.inFire, touchingCactus: contact.touchingCactus,
    respiration: enchLevel(inventory.armor[0], ENCH.RESPIRATION),
  });
  player.depthStrider = enchLevel(inventory.armor[3], ENCH.DEPTH_STRIDER);
  // status effects (game/effects): movement, jump, slow falling, night vision lifts the gamma
  player.speedMult = speedMult(stats.effects);
  // spear charge: use_modifiers.movement_modifier is 1.0 (bedrock-samples), so no slowdown: sprint and ride to build speed
  player.jumpBoost = jumpBoost(stats.effects);
  player.slowFalling = slowFalling(stats.effects);
  env.uGamma.value = Math.max(settings.brightness, nightVision(stats.effects, tickCounter));
  player.setSprintAllowed(stats.canSprint);
  // magma blocks burn whatever stands on them (fire-type damage, no ignition)
  if (!srv) {
    const get = (x: number, y: number, z: number): number => chunks.world.getBlock(x, y, z);
    const p = player.position;
    if (mode === 'survival' && magmaDamage(blockUnderFeet(get, p.x, p.y, p.z), player.onGround, player.sneaking, false) > 0) {
      stats.damage(1, { kind: 'fire' });
    }
    for (const m of entities.mobs) {
      if (!m.alive || !m.onGround) continue;
      const immune = (m as unknown as { fireImmune?: boolean }).fireImmune === true;
      if (magmaDamage(blockUnderFeet(get, m.x, m.y, m.z), true, false, immune) > 0) m.hurt(1);
    }
  }
  attackTicks++;
  // the shield is raised by crouching with one equipped (Minecraft: hold sneak)
  // ...or by holding right mouse (vanilla); it only counts after a 5-tick raise and while not disabled by an axe
  // vanilla: hold right mouse with a shield in either hand (main hand first) — not sneak
  // Bedrock: sneaking with a shield also raises it, and it blocks with no raise delay
  const bedrockShield = COMBAT_EDITION === 'bedrock';
  const wantBlock = (devBlock || (rightHeld && !use && pointerLocked) || (bedrockShield && player.sneaking && !player.flying)) && shieldStack() !== null && !stats.dead;
  shieldRaise = wantBlock ? shieldRaise + 1 : 0;
  if (shieldDisabled > 0) shieldDisabled--;
  blocking = wantBlock && (bedrockShield || shieldRaise > 5) && shieldDisabled === 0;
  srvCombat?.tick(rightHeld && pointerLocked, wantBlock, !isShield(inventory.held));
  if (player.sprinting && player.onGround && !player.inWater) particles.dust(player.position.x, Math.floor(player.position.y - 0.2) + 1, player.position.z, chunks.world.getBlock(Math.floor(player.position.x), Math.floor(player.position.y - 0.2), Math.floor(player.position.z)), 1, 0.6);
  tickUse();
  player.usingItem = (use !== null && use.kind !== 'spear') || blocking;
  player.useAnim = use?.kind === 'trident' ? 'spear' : use?.kind === 'spear' ? (['spear_charge', 'spear_tired', 'spear_drop'] as const)[chargeStageIndex(chargeStage(inventory.held.id, use.ticks))] : use?.kind === 'crossbow' ? 'crossbow_charge' : use?.kind === 'eat' ? 'eat'
    : inventory.held.id === I.CROSSBOW && inventory.held.charged ? 'crossbow_hold' : '';
  if (!srv) travel.tick(); // the server moves players between dimensions (actions/world.ts)
  if (srv) { srv.spawned = pending === null; srv.tick(player); entities.spawningEnabled = false; }
  if (chunks.dimension === 'nether' && pending === null && !travel.travelling && !srv) netherSpawner.tick();
  if (pending === null && !travel.travelling && !srv) {
    netherRt.tick(); endRt.tick(); endFight.tick(); witherRt.tick();
    if (chunks.dimension === 'overworld') trials.tick();
    if (chunks.dimension === 'end' && !stats.dead) gateways.tick(player.position.x, player.position.y, player.position.z);
  }
  rockets.tick();
  if (!srv) chunks.batchEdits(tickSims); // the server runs the block sims
  if (!srv) furnaces.tick((key, lit) => { setFurnaceBlockLit(furnaceHost, key, lit); });
  if (!srv && tickCounter % 10 === 0) reconcileFurnaceBlocks(furnaceHost, furnaces);
  if (tickCounter % 2 === 0) {
    furnaceEffects(furnaceHost, furnaces, player.position.x, player.position.z, 24,
      { flame: (x, y, z) => particles.flame(x, y, z), smoke: (x, y, z) => particles.fuse(x, y, z) });
  }
  if (!srv) brewing.tick();
  // a primed charge fizzes one smoke particle a tick from the top of its cube
  // (vanilla's PrimedTnt.tick), so the fuse is never silent-looking
  tnt.forEachCharge((c) => particles.fuse(c.x, c.y + 1, c.z));
  if (replica) replica.tick(); // server mode: no mob AI, spawning or item physics here
  if (pending === null) {
    if (!srv) {
      entities.tick();
      if (stats.dead || travel.travelling) { dismountRider(); leashes.releasePlayerHeld(true); }
      leashes.tick(leashCtx);
      ridingAfterMobs();
      boats.tick(stats.dead || travel.travelling);
      minecarts.tick();
    }
    {
      const rm = mountState.mob;
      hud.riding = rm !== null && mode === 'survival';
      rideHud.update(rm && mode === 'survival' ? { health: rm.health, maxHealth: rm.maxHealth, jump: mountState.jumpBar } : null);
    }
    if (!srv) { village.tick(); traders.tick(); }
    // server mode: projectiles, drops and orbs are the server's (drawn from the replica)
    if (!srv) {
      thrown.tick(thrownCtx);
      bobbers.tick(bobberCtx);
      potions.tick(potionCtx);
      items.tick(itemHost); xpOrbs.tick(xpHost);
      tridents.tick(tridentHost);
      tickRiptide();
    }
    enchantBooks.tick(player.position.x, player.position.y, player.position.z);
  }
  if (!srv && ++furnaceSaveTimer >= 20) {
    furnaceSaveTimer = 0;
    save.setFurnaces(furnaces.serialize());
    save.setStats(statsBlob());
  }
}

/** Third-person held-item meshes (main hand, off-hand), rebuilt when the item or its sprite variant changes. */
interface HeldSlot { mesh: THREE.Object3D | null; light: ((l: number) => void) | null; key: string }
const heldSlot: HeldSlot = { mesh: null, light: null, key: '' };
const offSlot: HeldSlot = { mesh: null, light: null, key: '' };
let tridentMat: THREE.ShaderMaterial | null = null;
/** Sprite variant of the held item in use (bow draw frame, crossbow load frame / loaded arrow). */
function heldVariant(id: number, stack: Stack): string {
  if (id === I.FISHING_ROD && bobbers.hook) return 'fishing_rod_cast';
  if (id === I.BOW && use?.kind === 'bow') return `bow_pulling_${use.ticks >= 18 ? 2 : use.ticks > 13 ? 1 : 0}`;
  if (id === I.CROSSBOW) {
    if (stack.charged) return stack.fw ? 'crossbow_firework' : 'crossbow_arrow';
    if (use?.kind === 'crossbow') {
      const p = use.ticks / Math.max(1, 25 - 5 * enchLevel(stack, ENCH.QUICK_CHARGE));
      return `crossbow_pulling_${p >= 1 ? 2 : p > 0.58 ? 1 : 0}`;
    }
  }
  return '';
}
function syncHeldMesh(slot: HeldSlot, id: number, stack: Stack, left: boolean): void {
  const variant = left ? '' : heldVariant(id, stack);
  const spearCharge = !left && isSpearId(id) && use?.kind === 'spear';
  const key = `${id}:${variant}${spearCharge ? ':charge' : ''}`;
  if (key === slot.key) return;
  slot.key = key;
  if (slot.mesh) { slot.mesh.removeFromParent(); slot.mesh = null; slot.light = null; }
  if (id <= 0) return;
  let mesh: THREE.Mesh;
  if (id === I.TRIDENT) {
    tridentMat ??= createLitMaterial(env, new THREE.TextureLoader().load(TRIDENT_TEX, (t) => {
      t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace; t.generateMipmaps = false;
    }), 'world', true);
    tridentMat.side = THREE.DoubleSide; // the hand frame mirrors, which flips the winding
    mesh = new THREE.Mesh(tridentGeometry(), tridentMat);
    mesh.frustumCulled = false;
    const m = mesh;
    setMeshLight(m, 1);
    slot.light = (l) => setMeshLight(m, l);
    applyThirdPerson(mesh, 'trident', left);
  } else if (isItem(id)) {
    const sprite = variant || (isSpearId(id) ? `${spriteOf(id)}_in_hand` : spriteOf(id));
    if (!sprite) return;
    const s = itemSpriteMesh(env, sprite, true);
    mesh = s.mesh;
    slot.light = (l) => { s.mat.uniforms.uLight.value = l; };
    applyThirdPerson(mesh, heldKindOfItem(id), left);
    // couched spear: the tip lies along the (levelled) forearm instead of pointing up from the fist
    if (spearCharge) { mesh.rotation.z = (235 * Math.PI) / 180; mesh.position.y = -0.5; }
  } else {
    // blocks: the cube (vanilla block display) or, for torches, flowers and other non-cubes, the flat tile
    const model = itemRenderer.models.get(id);
    mesh = new THREE.Mesh(model.geometry, model.material('world'));
    mesh.frustumCulled = false;
    const m = mesh;
    setMeshLight(m, 1);
    slot.light = (l) => setMeshLight(m, l);
    applyThirdPerson(mesh, model.block ? 'block' : 'flat', left);
  }
  (left ? playerModel.leftHand : playerModel.rightHand).add(mesh);
  slot.mesh = mesh;
}

// --- audio (src/audio): observes game state; the explicit calls below are sfx.* ---
const sfx = attachAudio({
  camera: player.camera, world: chunks.world, player, stats, tnt, itemHost, xpHost,
  mobs: () => entities.mobs, arrows: () => entities.arrows, charges: (fn) => tnt.forEachCharge(fn),
  mood: () => (!started ? 'menu' : chunks.dimension === 'nether' ? 'nether' : mode === 'creative' ? 'creative' : 'game'),
  paused: () => menus.open && !stats.dead && !net.session && !srv,
  dimension: () => chunks.dimension, armor: () => inventory.armor, itemName: nameOf, hud,
});

weatherRenderer.audio = sfx.am;
inventory.onChange = () => save.setInventory(inventory.serialize());
if (!save.inventory) inventory.changed();

function frame(): void {
  requestAnimationFrame(frame);
  const dt = Math.min(clock.getDelta(), 0.1);
  perf.frameStart();

  trySpawn();

  // A menu freezes a single-player world. In a session it must not: you are
  // standing in someone else's world, others can still hit you, and a frozen
  // tab stops sending states (so everyone else sees a red, motionless body).
  const paused = menus.open && !stats.dead && !net.session && !srv;
  const playing = started && !menus.open;

  // Safety net for the one state nothing else catches: playing, no mouse, no
  // panel. Every lock loss we know about shows the menu by itself; this is
  // what makes "sometimes Esc stops doing anything" self-healing.
  if (started && !pointerLocked && !overlayOpen()) {
    unlockedFor += dt;
    if (unlockedFor > 1) pauseIfStuck();
  } else {
    unlockedFor = 0;
  }

  // fixed 20 Hz game ticks; the camera interpolates between them
  if (!paused) physicsAcc += dt;
  while (physicsAcc >= TICK_DT) {
    gameTick();
    physicsAcc -= TICK_DT;
  }
  const alpha = physicsAcc / TICK_DT;
  player.updateCamera(alpha);
  sfx.update(dt);
  perf.mark('physics');

  chunks.update(player.position.x, player.position.z);
  if (worldReadyMs === 0 && chunks.stats.wanted > 0 && chunks.stats.ready >= chunks.stats.wanted) {
    worldReadyMs = performance.now() - loadStart;
  }
  perf.mark('chunks');
  env.uTime.value += dt;
  {
    const cp = player.camera.position;
    sky.eyeInLava = chunks.world.getBlock(Math.floor(cp.x), Math.floor(cp.y), Math.floor(cp.z)) === 23; // LAVA_ID
  }
  sky.update(paused ? 0 : dt, player.camera, player.headInWater, fogFar());
  const netherBiome = chunks.dimension === 'nether' ? netherGen.biomeAt(Math.floor(player.position.x), Math.floor(player.position.z)) : undefined;
  dimEnv.apply(chunks.dimension, fogFar(), player.headInWater, dt, netherBiome);
  weather.update(paused ? 0 : dt);
  weatherFx.enabled = chunks.dimension === 'overworld';
  envAmbience.apply(chunks.dimension, player.camera.position, dt, player.headInWater, sky.eyeInLava);
  weatherRenderer.update(paused ? 0 : dt, player.camera.position, chunks.dimension === 'overworld');
  netherParticles.update(paused ? 0 : dt, player.camera.position, chunks.dimension, netherBiome);
  travel.update(dt);

  // block highlight + auto-break
  player.camera.getWorldDirection(lookDir);
  const hit = player.spectator || stats.dead ? null : raycast(chunks.world, player.camera.position, lookDir, REACH);
  {
    // plain-text crosshair target for agent clients (agent-api/server.mjs)
    const m = hit ? mobInFront(hit, lookDir) : null;
    const c = player.camera.position;
    (window as unknown as { __lookAt?: number[] | null }).__lookAt = hit && !m ? [hit.x, hit.y, hit.z, hit.id] : null;
    (window as unknown as { __look?: string }).__look = m ? `a ${m.type} mob`
      : hit ? `${nameOf(hit.id) || 'block ' + hit.id} at ${hit.x} ${hit.y} ${hit.z}, ${Math.hypot(hit.x + 0.5 - c.x, hit.y + 0.5 - c.y, hit.z + 0.5 - c.z).toFixed(1)} blocks away`
      : 'nothing within reach';
  }
  // the targeted block's own shape (a torch post, a mushroom, a slab...), moved with a plant's jitter
  const hitBoxes = hit ? TARGET_BOXES[hit.id] ?? undefined : undefined;
  const hitJitter = hit !== null && PLANT_JITTER[hit.id] === 1;
  const hitX = hit ? (hitJitter ? hit.x + plantJitterX(hit.x, hit.z) : hit.x) : 0;
  const hitZ = hit ? (hitJitter ? hit.z + plantJitterZ(hit.x, hit.z) : hit.z) : 0;
  if (hit && pointerLocked) highlight.show(hitX, hit.y, hitZ, hitBoxes);
  else highlight.hide();

  // hold-to-break: progress accumulates on the targeted block at its vanilla
  // mining speed, resets when the target changes or the mouse is released
  if (breakCooldown > 0) breakCooldown -= dt;
  if (breakHeld && hit && pointerLocked && !paused) {
    if (!breakTarget || breakTarget.x !== hit.x || breakTarget.y !== hit.y || breakTarget.z !== hit.z) {
      breakTarget = { x: hit.x, y: hit.y, z: hit.z };
      breakProgress = 0;
    }
    const held = inventory.held;
    const secs = breakSecondsFx(hit.id, isEmpty(held) ? 0 : held.id, {
      onGround: player.onGround || player.flying, inWater: player.headInWater, creative: mode === 'creative',
      efficiency: enchLevel(held, ENCH.EFFICIENCY), aquaAffinity: enchLevel(inventory.armor[0], ENCH.AQUA_AFFINITY) > 0,
    });
    srvBlocks?.dig(Number.isFinite(secs) ? hit : null); // server mode: dig start / abort
    if (!Number.isFinite(secs)) {
      breakProgress = 0;
      breakOverlay.clear();
    } else if (breakCooldown <= 0) {
      breakProgress += secs <= 0 ? 1 : dt / secs;
      if (breakProgress >= 1) {
        if (srvBlocks) srvBlocks.digFinish(hit); else breakBlock(hit);
        breakProgress = 0;
        breakTarget = null; // next frame re-locks onto whatever is behind it
        breakCooldown = mode === 'creative' ? BREAK_COOLDOWN : 0; // Bedrock survival has no delay between breaks
        breakOverlay.clear();
        player.startSwing();
      } else {
        breakOverlay.set(hitX, hit.y, hitZ, breakProgress, hitBoxes);
        particles.crack(hit.x, hit.y, hit.z, hit.id, hit.nx, hit.ny, hit.nz, dt);
        sfx.blockHit(hit.x, hit.y, hit.z, hit.id);
        if (player.swingProgress === 0) player.startSwing();
      }
    }
  } else {
    breakProgress = 0;
    breakTarget = null;
    breakOverlay.clear();
    srvBlocks?.dig(null);
  }

  // --- autosave: record player + time every frame; WorldSave throttles the
  // actual localStorage write to once per second, and blocks are recorded
  // eagerly by the chunk manager at edit time.
  if (!discardSave && pending === null) {
    save.weather = weather.serialize();
    save.setPlayer({
      x: player.position.x, y: player.position.y, z: player.position.z,
      yaw: player.yaw, pitch: player.pitch, flying: player.flying,
    }, sky.time);
    save.flush();
  }

  particles.update(dt);
  hearts.update(dt);
  srvTiles?.frame();
  if (srvDraw) srvDraw.frame(alpha, dt, performance.now() / 1000, tickCounter); // thrown things, tridents, hooks, the End fight: the server's
  else {
    thrownRenderer.update(thrown.eggs, alpha, dt);
    bobberRenderer.update(bobbers.hook, alpha, dt, rodTip());
    potionRenderer.update(potions.potions, potions.clouds, alpha, dt, performance.now() / 1000);
  }
  // primed charges fly, blink and (eventually) blow up
  primedTnt.update(tnt.charges);
  fallingView.update(replica
    ? replica.others('falling').map((o) => ({ key: o.id, id: Number((o.record as number[])[3]) || 0, x: o.x, y: o.y, z: o.z, px: o.px, py: o.py, pz: o.pz }))
    : falling.blocks.map((b) => ({ key: b, id: b.id, x: b.x, y: b.y, z: b.z, px: b.px, py: b.py, pz: b.pz })), alpha);
  boatView.update(replica ? replica.boats : boats.sim.boats, alpha, performance.now() / 1000);
  minecartView.update(minecarts.sim.carts, alpha, chunks.dimension);
  ambient.update(dt, player.camera.position);

  // mobs, drops, orbs, the player's body and hand
  const cam = player.camera.position;
  entityRenderer.setViewer(cam.x, cam.y, cam.z);
  // the nether's ambient light (no sky light there) also reaches mobs
  if (chunks.dimension === 'nether') for (const v of entities.mobViews()) v.light = Math.max(v.light, 0.55);
  entityRenderer.update(entities.mobViews(), replica ? replica.arrowViews() : entities.arrowViews(), alpha);
  netherRt.frame(alpha);
  endRt.frame(alpha);
  endFight.render(alpha);
  witherRt.frame(alpha);
  if (replica) itemRenderer.update(replica.itemViews(), replica.orbViews(), alpha, player.camera);
  else itemRenderer.update(items.views(), xpOrbs.views(), alpha, player.camera);
  srvBossBar?.update(srvBoss, hudStage.width);

  const heldRaw = isEmpty(inventory.held) ? 0 : inventory.held.id;
  // a shield is drawn by its own views, not as a held sprite
  const heldId = heldRaw === I.SHIELD ? 0 : heldRaw;
  { const el = elytraStack(); player.hasElytra = !!el && el.dmg < itemDef(el.id).maxDamage! - 1; }
  player.setEquipment(heldId, inventory.armor.map((s) => (isEmpty(s) ? 0 : s.id)));
  player.onFire = stats.onFire;
  player.light = lightAt(player.position.x, player.position.y + 1, player.position.z);
  const bodyVisible = !player.isFirstPerson && !player.spectator && started;
  playerModel.setVisible(bodyVisible);
  if (bodyVisible) {
    playerModel.update(player.poseView(alpha), alpha);
    syncHeldMesh(heldSlot, heldId, inventory.held, false);
    // a shield is drawn by its own model, never as an off-hand sprite
    const offId = isEmpty(inventory.offhand) || inventory.offhand.id === I.SHIELD ? 0 : inventory.offhand.id;
    syncHeldMesh(offSlot, offId, inventory.offhand, true);
    heldSlot.light?.(Math.max(player.light, 0.12));
    offSlot.light?.(Math.max(player.light, 0.12));
  }
  const shielded = shieldStack() !== null;
  playerModel.setShield(shielded, blocking);
  sweepFx.update(dt);
  if (!srvDraw) tridentView.update([...tridents.views(), ...entities.mobTridentViews()], alpha);
  spitView.update(spits.views(), alpha);
  enchantBooks.update(alpha, (x, y, z) => lightAt(x, y, z));
  leashView.update(leashes.links(), leashes.knots(), alpha, {
    entityById: (id) => entities.mobs.find((m) => m.id === id),
    light: lightAt,
    player: () => {
      // the lead hangs from the right hand: a little right, down and ahead of the eyes
      const c = player.camera.position, y = player.yaw;
      return [c.x + Math.cos(y) * 0.3 - Math.sin(y) * 0.35, c.y - 0.5, c.z - Math.sin(y) * 0.3 - Math.cos(y) * 0.35];
    },
  });
  tickGlint(dt);
  shieldView.update(dt, { has: shielded, blocking, hidden: !player.isFirstPerson || player.spectator || stats.dead || !started || menus.open, light: player.light });
  hand.update(dt, {
    heldItem: heldId === 0 ? null : inventory.held,
    offhand: inventory.offhand,
    swing: player.swingProgress,
    usingItem: use ? use.kind : 0,
    rodCast: !!bobbers.hook,
    spearStage: use?.kind === 'spear' ? chargeStageIndex(chargeStage(inventory.held.id, use.ticks)) : 0,
    useProgress: use ? (use.kind === 'eat' ? use.ticks / eatTicksOf(inventory.held.id) : use.kind === 'crossbow' ? use.ticks / Math.max(1, 25 - 5 * enchLevel(inventory.held, ENCH.QUICK_CHARGE)) : use.ticks / 20) : 0,
    chargeTicks: Math.max(1, 25 - 5 * enchLevel(inventory.held, ENCH.QUICK_CHARGE)),
    sneaking: player.sneaking,
    // dims with the world but never goes fully black (a small floor for unlit caves)
    light: Math.max(player.light, 0.3),
    ...handLight(),
    bobPhase: player.bobPhase,
    bobAmount: player.bobAmount,
    hidden: !player.isFirstPerson || player.spectator || stats.dead || !started || menus.open,
    cooldown: attackCharge(),
    yaw: player.yaw,
    pitch: player.pitch,
  });

  // HUD
  const showHud = playing || (started && menus.open);
  hud.setVisible(started && (showHud || stats.dead));
  hud.setWater(player.headInWater);
  // vanilla: gaining or spending XP hides the locator bar for 100 ticks
  const xpNow = `${stats.xpLevel}:${stats.xpProgress.toFixed(2)}`;
  if (xpNow !== xpSig) {
    if (xpSig) xpShownUntilTick = tickCounter + 100;
    xpSig = xpNow;
  }
  hud.update({
    health: stats.health, maxHealth: stats.maxHealth, food: stats.food, saturation: stats.saturation,
    armor: armorOf().defense, air: stats.air, maxAir: stats.maxAir,
    xpLevel: stats.xpLevel, xpProgress: stats.xpProgress, creative: mode === 'creative',
    attackCooldown: attackCharge(), tick: tickCounter, hurtFlash: stats.hurtFlash, lowHealth: stats.lowHealth,
    locator: tickCounter < xpShownUntilTick ? null : locatorWaypoints(),
    heartStyle: heartStyleOf((id) => stats.effects.has(id)), hungry: stats.effects.has(FX.HUNGER),
    regen: stats.effects.has(FX.REGENERATION) && stats.health < stats.maxHealth,
    showCooldownBar: itemDef(inventory.held.id).cooldownBar === true,
  });
  fxHud.update(stats.effects, {
    absorption: stats.absorption, armor: armorOf().defense, creative: mode === 'creative',
    inventoryOpen: screens.inventoryOpen && mode !== 'creative', screenOpen: screens.open, tick: tickCounter,
  });
  screens.update(dt);
  windows?.update();
  menus.update(dt);

  // agent clients (agent-api) get the debug text without the overlay being drawn into their frames
  if (debugOn || (window as unknown as { __agent?: boolean }).__agent) {
    const p = player.position;
    const biomeInfo = hudTerrain.columnInfo(Math.floor(p.x), Math.floor(p.z));
    const pcx = Math.floor(p.x / CHUNK_SIZE), pcz = Math.floor(p.z / CHUNK_SIZE);
    if (pcx !== structChunkX || pcz !== structChunkZ) {
      structChunkX = pcx;
      structChunkZ = pcz;
      nearest = hudTerrain.nearestStructures(p.x, p.z, 5);
    }
    const es = entities.stats();
    const debugLines = [
      `XYZ ${p.x.toFixed(1)} / ${p.y.toFixed(1)} / ${p.z.toFixed(1)}`,
      `chunks ${chunks.stats.chunks}  gen ${chunks.stats.genJobs}  mesh ${chunks.stats.meshJobs}`,
      `fly ${player.flying ? 'on' : 'off'}   ground ${player.onGround}   water ${player.inWater}   mode ${mode}`,
      chunks.dimension === 'end'
        ? 'dimension minecraft:the_end   biome minecraft:the_end'
        : chunks.dimension === 'nether'
        ? `dimension minecraft:the_nether   biome minecraft:${NETHER_BIOME_NAMES[netherGen.biomeAt(Math.floor(p.x), Math.floor(p.z))]}`
        : `dimension minecraft:overworld   biome ${BIOME_NAMES[biomeInfo.biome as Biome]}   h ${biomeInfo.height}   time ${((sky.time * 24 + 6) % 24).toFixed(1)}h`,
      `selected ${nameOf(heldId) || '—'}   water updates ${fluids.pending}   fuses ${tnt.pending}`,
      `mobs ${es.mobs} (${es.hostile} hostile)   items ${items.count}   orbs ${xpOrbs.count}`,
      ...nearest.map((s) =>
        `${s.name}  ${Math.round(s.dist)}m ${compassOf(s.x - p.x, s.z - p.z)}  (${Math.floor(s.x)} ${Math.floor(s.y)} ${Math.floor(s.z)})`
      ),
    ];
    if (debugOn) hud.updateDebug(debugLines);
    // plain-text mirror of the canvas-drawn overlay for agent clients (agent-api/server.mjs)
    (window as unknown as { __debugText?: string }).__debugText = debugLines.join('\n');
    (window as unknown as { __yaw?: number }).__yaw = player.yaw; // radians; lets agent `goto` steer
  }
  if (!debugOn) {
    // coordinates readout (Options > Show Coordinates), vanilla F3 wording
    const p = player.position;
    hud.setCoords(settings.showCoords
      ? `XYZ: ${p.x.toFixed(3)} / ${p.y.toFixed(3)} / ${p.z.toFixed(3)}\n` +
        `Block: ${Math.floor(p.x)} ${Math.floor(p.y)} ${Math.floor(p.z)}\n` +
        `Facing: ${facing(player.yaw)}`
      : '');
  }

  perf.mark('misc');

  renderer.info.reset();
  if (quality.shadowCascades > 0) {
    shadows.update(shadowScene, player.camera, env.uSunDir.value, chunks.version);
    perf.mark('shadows');
  }
  net.frame(); // remote players + chat overlay
  chat.update(); // fade chat lines (single player: nothing else drives it)
  // Agent clients (agent-api) run the full game but never look at the canvas:
  // drawing is ~98% of a software-rendered frame, so skip it unless a picture
  // was asked for (__agentRender counts frames to draw).
  const aw = window as unknown as { __agent?: boolean; __agentRender?: number };
  if (!aw.__agent || (aw.__agentRender ?? 0) > 0) {
    if (aw.__agentRender) aw.__agentRender--;
    post.render(scene, player.camera);
  }
  perf.mark('render');
  perf.frameEnd();
}

// touch devices: on-screen controls drive the same key / mouse paths (see ui/touchControls)
initTouchControls({
  canvas: renderer.domElement,
  player,
  isPlaying: () => started && pointerLocked && !overlayOpen(),
  panelOpen: () => screens.open || tradeScreen.open || horseScreen.open || llamaScreen.open || teleportMenu.open,
  targetKind: () => {
    const dir = player.camera.getWorldDirection(lookTmp);
    return mobInFront(raycast(chunks.world, player.camera.position, dir, REACH), dir) ? 'mob' : 'other';
  },
  holdUses: () => {
    const h = inventory.held;
    return !isEmpty(h) && (h.id === I.BOW || h.id === I.CROSSBOW || h.id === I.TRIDENT || h.id === I.SHIELD || !!itemDef(h.id).food);
  },
  screenStage: screens.stage.el,
});

// The `visual` build mode is used by scripts/visual/*: it bundles the app
// (so a capture is immune to other agents editing the source mid-run) but
// still exposes the automation handle. Plain production builds do not.
if (import.meta.env.DEV || import.meta.env.MODE === 'visual') {
  // dev console / automation handle
  (window as unknown as Record<string, unknown>).__oc = {
    srv, srvBlocks, replica, srvDraw, sleepUi, srvBossBar, bossNow: () => srvBoss,
    SHAPE_FAMILIES, shapeId, perf, chunks, fluids, fires, tnt, falling, fallingView, primedTnt, isFlashing, player, FIRE_ID, TNT_ID, PLANKS_ID, TORCH_ID,
    FLINT_AND_STEEL_ID, sky, renderer, scene, hud, env, post, shadows, cycleQuality, quality: () => quality,
    worldReadyMs: () => worldReadyMs, CHEST_FACING_0, CHEST_OPEN_FACING_0, BED_FOOT_ID, BED_HEAD_ID,
    FURNACE_FACINGS, THREE, inventory, stats, entities, items, xpOrbs, screens, menus, settings, furnaces, interact, attackMob, breakBlock, tridents, throwTrident, fireCrossbow, ENCHANTING_TABLE_ID, ANVIL_FAMILIES, SMITHING_TABLE_ID, startUse, gameTick, setRightHeld: (b: boolean) => { rightHeld = b; }, getUse: () => use, hand, bobbers,
    I, setMode: applyMode, startGame, playerModel, MOB_TYPES, boats, minecarts,
    mounts: { mountMobAsPlayer, dismountRider, mountState },
    leash: { leashes, leadUseOnMob, leadUseOnFence, isFence, leadEnv },
    started: () => started, locked: () => pointerLocked, tryLock, lockLog,
    enterBed, leaveBed, bedIds: [BED_FOOT_ID, BED_HEAD_ID],
    block: (b: boolean) => { devBlock = b; }, sfx, playerTarget, enchantBooks,
    travel, save, netherRt, endFight, village, tradeScreen, hudTerrain, traders, llamaScreen, spits,
    // shared chat overlay (single player + multiplayer): send echoes or relays,
    // echo() prints a line directly. The net layer should use this instance.
    chat, sendChat, echo: (name: string, text: string, system = false) => chat.add(name, text, system),
    // the /pin gate (ui/chatCommands) stays shut in a real page; dev and
    // visual runs unlock it so a scenario can drive commands without the pin
    unlockChatCommands, weather, netherGen, netherParticles, FLOWING_LAVA_IDS, rideHud, gameHud: hud, getMode: () => mode,
  };
}

// Agent clients (agent-api): describe the live game as text. See src/agent/agentApi.ts.
if ((window as unknown as { __agent?: boolean }).__agent) {
  installAgentApi({
    world: () => chunks.world,
    eye: () => player.camera.position,
    feet: () => player.position,
    yaw: () => player.yaw,
    pitch: () => player.pitch,
    flags: () => ({ onGround: player.onGround, inWater: player.inWater, flying: player.flying }),
    inventory, stats,
    mobs: () => entities.mobs.filter((m) => !m.dying).map((m) => ({ type: m.type, hostile: isHostileType(m.type), health: m.health, x: m.x, y: m.y, z: m.z })),
    items: () => items.items.filter((it) => !it.removed),
    players: () => (net.session?.remotePlayers() ?? []).map((r) => ({ name: r.name, x: r.state.x, y: r.state.y, z: r.state.z })),
    look: () => (window as unknown as { __look?: string }).__look ?? 'nothing within reach',
    target: () => (window as unknown as { __lookAt?: number[] | null }).__lookAt ?? null,
    holding: () => player.isFrozen,
    notes: () => agentNotes.splice(0),
    noteCount: () => agentNotes.length,
    biome: () => (chunks.dimension === 'overworld'
      ? BIOME_NAMES[hudTerrain.columnInfo(Math.floor(player.position.x), Math.floor(player.position.z)).biome as Biome]
      : chunks.dimension),
    hours: () => (sky.time * 24 + 6) % 24,
    mode: () => mode,
    dimension: () => chunks.dimension,
  });
  (window as unknown as { __agentRespawn?: () => void }).__agentRespawn = () => { if (stats.dead) { respawn(); menus.hide(); tryLock(); } };
}

srv?.live(); // every subscriber exists: hand over what the server said while the page was built
frame();

// Last-chance save: the loop throttles writes to once a second, so quitting
// (or reloading) right after picking a block or moving would otherwise drop
// that change. pagehide covers reload/close/back-forward; the write is a
// synchronous localStorage set.
window.addEventListener('pagehide', () => {
  if (discardSave) return;
  save.setFurnaces(furnaces.serialize());
  save.setStats(statsBlob());
  save.flush(true);
});
